import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  AlertTriangle,
  ArrowLeft,
  Calendar,
  Check,
  CircleDollarSign,
  FileText,
  HelpCircle,
  Eye,
  Loader2,
  MapPin,
  Package,
  Plane,
  RotateCcw,
  Route as RouteIcon,
  Save,
  Send,
  ShieldAlert,
  Sparkles,
} from "lucide-react";
import { toast } from "sonner";
import AppLayout from "@/components/AppLayout";
import { ErrorState } from "@/components/States";
import { Btn, inputCls } from "@/components/Primitives";
import { GovBadge } from "@/components/GovBadge";
import { api, confirmAction, formatApiErrorDetail } from "@/lib/api";
import { inr } from "@/lib/format";

const QUICK = ["Make Day 3 easier", "Reduce cost by 10%", "Add one rest day", "Increase the group to 25", "Make it suitable for beginners"];
const EXAMPLE = "Create a six-day moderate Himalayan trek for 20 participants with a budget of INR 25,000 per person.";
const ACTIVE_AI_GENERATION_KEY = "trailpulse.aiPlanner.activeGenerationId";
const AI_PLANNER_DRAFT_KEY = "trailpulse.aiPlanner.draftState";

const DEFAULT_FOUNDATION = {
  place: "",
  origin: "",
  start_date: "",
  end_date: "",
  budget: "",
  max_seats: "",
  budget_scope: "",
  stay_quality: "",
  room_sharing: "",
  comfort_level: "",
};

const FOUNDATION_FIELDS = [
  { key: "place", label: "Destination / route", placeholder: "e.g. Meghalaya, Spiti, Bali" },
  { key: "origin", label: "Starting city", placeholder: "e.g. Mumbai" },
  { key: "max_seats", label: "Group count", type: "number", placeholder: "20" },
  { key: "budget", label: "Budget per person", type: "number", placeholder: "45000" },
  { key: "budget_scope", label: "Budget scope", type: "select", options: ["Includes return flights", "Land-only after arrival", "Not sure yet"] },
  { key: "stay_quality", label: "Stay quality", type: "select", options: ["Budget", "Standard", "Good mid-range", "Premium"] },
  { key: "room_sharing", label: "Room sharing", type: "select", options: ["Twin sharing", "Triple sharing", "Quad sharing", "Flexible"] },
  { key: "comfort_level", label: "Trip comfort", type: "select", options: ["Easy", "Balanced", "Active"] },
];

const DETAIL_CHIPS = [
  {
    label: "Easier travel days",
    helper: "Shorter drives, fewer hotel changes",
    value: "Optimize for less travel fatigue with shorter drives, fewer hotel changes, and realistic rest time.",
  },
  {
    label: "Fewer places, better experience",
    helper: "Avoid a rushed checklist route",
    value: "Keep the route focused instead of covering too many places just because they are popular.",
  },
  {
    label: "Show budget tradeoffs",
    helper: "Tell what the budget may compromise",
    value: "Call out budget tradeoffs honestly, including hotel quality, vehicle comfort, flights, and activity choices.",
  },
  {
    label: "Add backup time",
    helper: "For rain, traffic, and transfers",
    value: "Add weather, road, and transfer buffers so the plan has backup time if conditions change.",
  },
  {
    label: "Beginner-friendly",
    helper: "Easy pace, optional hard activities",
    value: "Prefer beginner-aware pacing with easier days and optional strenuous activities.",
  },
];

const PROGRESS_CONFIG = {
  preflight: {
    eyebrow: "Planner checks",
    title: "Checking your brief and preparing smart choices",
    detail: "We are separating facts you already gave from decisions that can improve the trip.",
    stages: [
      { label: "Reading brief", detail: "Destination, dates, budget, people", icon: FileText },
      { label: "Season check", detail: "Weather, access, activity fit", icon: Calendar },
      { label: "Route options", detail: "Popular places vs best-fit days", icon: RouteIcon },
      { label: "Risk scan", detail: "Safety, permits, group operations", icon: ShieldAlert },
    ],
    tips: [
      "If your timing has weather risk, we will show both choices: keep your dates safely or switch to a stronger season.",
      "The route is judged by days available, transfer fatigue, and group movement, not only by famous places.",
      "We skip questions when your brief or trip basics already gave the answer.",
      "Warnings are meant to protect the experience, not restrict the traveler.",
    ],
  },
  generation: {
    eyebrow: "Draft build",
    title: "Turning your choices into a complete trip plan",
    detail: "This takes longer because the draft includes route logic, logistics, costs, risks, and day-by-day structure.",
    stages: [
      { label: "Route spine", detail: "Hubs, drives, arrival buffers", icon: MapPin },
      { label: "Experience fit", detail: "Pace, places, fitness level", icon: Sparkles },
      { label: "Operations", detail: "Vehicles, guides, rooms, meals", icon: Package },
      { label: "Budget polish", detail: "Cost tradeoffs and final checks", icon: CircleDollarSign },
    ],
    tips: [
      "A good group trip plan thinks about vehicle flow, guide ratio, meal timing, and check-in delays from day one.",
      "We use your selected choices to shape the draft, so the plan stays flexible without becoming generic.",
      "Safety and permit notes are pulled into the plan so the organizer can act before bookings start.",
      "The final draft should explain tradeoffs clearly, especially where comfort, cost, and time compete.",
    ],
  },
};

function asArray(value) {
  return Array.isArray(value) ? value : [];
}

function isPlaceExplorationQuestion(question) {
  const text = `${question.field} ${question.question}`.toLowerCase();
  return /(place|places|visit|route|stop|stops|attraction|attractions)/.test(text) && !/(destination|region)/.test(text);
}

function isQuestionAnswered(question, answers) {
  return Boolean(String(answers?.[question.field] || "").trim());
}

function blockingQuestions(questions, answers) {
  return asArray(questions).filter((question) => (
    !isQuestionAnswered(question, answers) && !isPlaceExplorationQuestion(question)
  ));
}

function shouldRefreshAfterQuestion(question = {}) {
  const text = `${question.field || ""} ${question.question || ""} ${question.why || ""}`.toLowerCase();
  return /\b(destination|place|route|must.?have|visit|season|month|date|timing|origin|start|airport|budget|group|people|traveler|traveller|fitness|difficulty|pace|hotel|stay|transport|vehicle|permit|access|safety)\b/.test(text);
}

function shouldRefreshAfterRecommendation(group = {}, guidance = "") {
  const key = String(group.key || "").toLowerCase();
  const text = `${key} ${guidance}`.toLowerCase();
  return (
    ["season_timing", "places_route_scope", "budget_quality", "group_operations", "fitness_readiness"].includes(key) ||
    /\b(season|timing|route|place|budget|group|fitness|safety|permit|access|hotel|stay|transport|vehicle)\b/.test(text)
  );
}

function buildRecommendationGroups(recommendations) {
  return asArray(recommendations).reduce((groups, rec, index) => {
    const key = rec.option_group || `single-${index}`;
    const existing = groups.find((group) => group.key === key);
    const item = { ...rec, originalIndex: index };
    if (existing) existing.items.push(item);
    else groups.push({ key, items: [item] });
    return groups;
  }, []);
}

function expandSeasonMonthRecommendations(recommendations, factPack = {}) {
  const bestMonths = asArray(factPack.best_months).filter(Boolean).slice(0, 3);
  if (bestMonths.length < 2) return asArray(recommendations);
  const expanded = asArray(recommendations).map((rec) => {
    const tags = new Set(asArray(rec.choice_tags).map((tag) => String(tag).toLowerCase()));
    const advised = String(rec.option_group || "").toLowerCase() === "season_timing" && (
      String(rec.option_label || "").toLowerCase().includes("advised") || tags.has("ideal_timing")
    );
    if (!advised || tags.has("flexible_timing")) return rec;
    return {
      ...rec,
      title: `Recommended months: ${bestMonths.join(", ")}`,
      advice: `Compare the recommended months below, then choose one exact month or let AI select the strongest fit for the route, weather, crowds, activities, and trip value.`,
      month_options: bestMonths,
    };
  });
  let keptRequested = false;
  let keptAdvised = false;
  return expanded.filter((rec) => {
    if (String(rec.option_group || "").toLowerCase() !== "season_timing") return true;
    const label = String(rec.option_label || "").toLowerCase();
    const advised = label.includes("advised") || asArray(rec.month_options).length > 0;
    if (advised) {
      if (keptAdvised) return false;
      keptAdvised = true;
      return true;
    }
    if (keptRequested) return false;
    keptRequested = true;
    return true;
  });
}

function seasonMonthChoice(rec, value, factPack = {}) {
  const months = asArray(rec?.month_options).filter(Boolean);
  const destination = factPack.destination || "the destination";
  if (value === "__ai__") {
    return {
      ...rec,
      title: `Let AI decide: ${months.join(", ")}`,
      apply_text: `Final timing choice: use AI-advised ideal timing and let the AI choose the strongest exact month from ${months.join(", ")} for ${destination}.`,
      choice_tags: [...asArray(rec.choice_tags), "ideal_timing", "ai_decides_month"],
    };
  }
  return {
    ...rec,
    title: `Choose ${value}`,
    apply_text: `Final timing choice: use AI-advised ideal timing and plan ${destination} in ${value}.`,
    choice_tags: [...asArray(rec.choice_tags), "ideal_timing", `ideal_month_${String(value).toLowerCase().replace(/\s+/g, "_")}`],
  };
}

function recommendationGuidanceOptions(group, factPack = {}) {
  return asArray(group?.items).flatMap((item) => {
    const guidance = [item.apply_text || item.advice];
    const monthOptions = asArray(item.month_options);
    if (!monthOptions.length) return guidance;
    return [
      ...guidance,
      ...monthOptions.map((month) => seasonMonthChoice(item, month, factPack).apply_text),
      seasonMonthChoice(item, "__ai__", factPack).apply_text,
    ];
  }).filter(Boolean);
}

function isRecommendationGroupDone(group, selectedRecommendations) {
  const selected = new Set(selectedRecommendations || []);
  return group.items.some((item) => selected.has(item.apply_text || item.advice));
}

function seasonChoiceContext(selectedRecommendations, factPack = {}) {
  const chosen = (selectedRecommendations || []).join(" \n ").toLowerCase();
  const bestMonths = asArray(factPack?.best_months).filter(Boolean).slice(0, 3);
  const chosenMonth = bestMonths.find((month) => chosen.includes(` in ${String(month).toLowerCase()}`));
  const aiChoosesMonth = /let (?:the )?ai choose|ai choose the strongest exact month/.test(chosen);
  const idealPeriod = chosenMonth || (aiChoosesMonth && bestMonths.length ? bestMonths.join(", ") : factPack?.ideal_period) || "the AI-advised season";
  const requestedPeriod = String(factPack?.requested_period || "").toLowerCase();
  const choseIdealPeriod = /(shift|switch|target|prefer planning|stronger season|ai[- ]advised|ideal timing|best experience|avoid poor timing)/i.test(chosen);
  const keptRequested = /(respect|requested|keep).*(timing|season|month|dates)|weather-safe|season-safe/.test(chosen);
  return {
    shiftedToIdeal: choseIdealPeriod && !keptRequested,
    keptRequestedSeason: keptRequested,
    aiChoosesMonth,
    idealPeriod,
    requestedPeriod,
  };
}

function selectedChoiceTags(selectedRecommendations, recommendations = [], answers = {}) {
  const selected = new Set(selectedRecommendations || []);
  const tags = new Set();
  const selectedModelTexts = [];
  asArray(recommendations).forEach((rec) => {
    const guidance = rec.apply_text || rec.advice;
    if (selected.has(guidance)) {
      selectedModelTexts.push(guidance);
      asArray(rec.choice_tags).forEach((tag) => tags.add(tag));
    }
  });
  const text = [
    ...selectedModelTexts,
    ...Object.entries(answers || {}).map(([key, value]) => `${key}: ${value}`),
  ].join(" \n ").toLowerCase();
  if (/(shift|switch|target|prefer|stronger|ideal|best experience|ai[- ]advised)/i.test(text)) tags.add("ideal_timing");
  if (/(respect|requested|keep).*(timing|season|month|dates)|weather-safe|season-safe/i.test(text)) tags.add("requested_timing");
  if (/(best-fit|fewer bases|reduce fatigue)/i.test(text)) tags.add("best_fit_route");
  if (/(popular|famous|requested places|anchors|must-have)/i.test(text)) tags.add("popular_route");
  if (/(active|trek|hike|fitness|strenuous)/i.test(text)) tags.add("active_trip");
  if (/(relaxed|beginner|easy|low fatigue)/i.test(text)) tags.add("relaxed_trip");
  if (/(luxury|premium|boutique|resort|4 star|5 star)/i.test(text)) tags.add("premium_stays");
  if (/(budget|cheap|low cost|economy|hostel|homestay)/i.test(text)) tags.add("budget_stays");
  if (/(flight|airport|fly|fare)/i.test(text)) tags.add("flight_sensitive");
  if (/(family|senior|kids|children|elderly)/i.test(text)) tags.add("comfort_sensitive");
  return tags;
}

function isInternalRecommendation(rec = {}) {
  const text = `${rec.title || ""} ${rec.advice || ""} ${rec.apply_text || ""}`.toLowerCase();
  return (
    text.includes("separate facts from travel trends") ||
    text.includes("fact-based inputs first") ||
    text.includes("trend-based inputs second") ||
    text.includes("mark current confirmation tasks")
  );
}

function recommendationIsRelevant(rec, tags) {
  if (isInternalRecommendation(rec)) return false;
  if (String(rec.option_group || "").toLowerCase() === "season_timing") return true;
  const has = (tag) => tags.has(tag);
  const hidden = asArray(rec.hidden_if_any);
  if (hidden.some(has)) return false;
  const all = asArray(rec.depends_on_all);
  if (all.length > 0 && !all.every(has)) return false;
  const any = asArray(rec.depends_on_any);
  if (any.length > 0 && !any.some(has)) return false;
  return true;
}

function isStaleSeasonText(text, context) {
  if (!context?.shiftedToIdeal) return false;
  if (context.requestedPeriod && String(text || "").toLowerCase().includes(context.requestedPeriod)) return true;
  return /\b(may(?:\s+20\d{2})?|pre-monsoon|monsoon|rain|rains|rainy|mist|wet months?|wet season|showers?|weather disruption|weather-safe|rain-safe|dawki river clarity)\b/i.test(String(text || ""));
}

function isSeasonTimingQuestion(question) {
  const text = `${question?.field || ""} ${question?.question || ""} ${question?.why || ""}`;
  return /\b(date|dates|month|season|timing|travel window|when.*travel|planning to travel)\b/i.test(text);
}

function isBookingSelectionQuestion(question) {
  const text = `${question?.field || ""} ${question?.question || ""} ${question?.why || ""}`.replace(/_/g, " ");
  return /\b(flight timing|flight preference|flight time|departure time|arrival time|start date|end date|departure date|return date|exact (?:travel )?dates?|airline|nonstop|layover|baggage|flight number|flight fare|cheapest flight|preferred airport|hotel name|hotel brand|specific hotel|check[- ]?in time|check[- ]?out time|room inventory)\b/i.test(text);
}

function isLowValuePlaceQuestion(question) {
  const text = `${question?.field || ""} ${question?.question || ""} ${question?.why || ""}`.replace(/_/g, " ");
  const asksForPlaces = /\b(place|places|spot|spots|stop|stops|attraction|attractions|sight|sights|route|must have|visit)\b/i.test(text)
    && /\b(which|choose|prefer|add|include|skip|visit|cover)\b/i.test(text);
  const explainsMaterialConflict = /\b(strenuous|difficult|fitness|mobility|accessible|accessibility|comfort|long walk|steep|altitude|safety|unsafe|seasonal|closed|closure|permit|restricted|long drive|rough road|weather risk)\b/i.test(text);
  return asksForPlaces && !explainsMaterialConflict;
}

function monthExperiencesForRecommendation(rec, factPack) {
  if (String(rec?.option_group || "").toLowerCase() !== "season_timing") return [];
  const entries = asArray(factPack?.month_experiences).filter((item) => item?.month && item?.experience);
  if (!entries.length) return [];
  const tags = new Set(asArray(rec?.choice_tags).map((tag) => String(tag).toLowerCase()));
  const label = String(rec?.option_label || "").toLowerCase();
  const requested = label.includes("user") || label.includes("request") || tags.has("requested_timing");
  const bestMonths = asArray(factPack?.best_months).slice(0, 3);
  const exactMonthTag = Array.from(tags).find((tag) => tag.startsWith("ideal_month_"));
  const exactRecommendedMonth = exactMonthTag
    ? bestMonths.find((month) => exactMonthTag === `ideal_month_${String(month).toLowerCase().replace(/\s+/g, "_")}`)
    : null;
  const targets = requested
    ? [factPack?.requested_period]
    : exactRecommendedMonth && !tags.has("ai_decides_month")
      ? [exactRecommendedMonth]
      : bestMonths;
  return entries.filter((item) => targets.some((target) => {
    const month = String(item.month || "").toLowerCase();
    const period = String(target || "").toLowerCase();
    return month && period && (month.includes(period) || period.includes(month));
  }));
}

function adaptRecommendationForSeason(rec, context) {
  if (!context?.shiftedToIdeal || !rec) return rec;
  const combined = `${rec.title} ${rec.advice} ${rec.evidence} ${rec.apply_text}`;
  if (!isStaleSeasonText(combined, context)) return rec;
  if (!rec.option_group) return null;
  return {
    ...rec,
    advice: String(rec.advice || "").replace(/\b(?:May|monsoon|pre-monsoon|rain|rains|rainy|mist|wet months?)\b/gi, "chosen season").trim(),
    evidence: `Season choice is now ${context.idealPeriod}; judge this by trip length, transfer time, group size, comfort, and activity fit instead of May or monsoon constraints.`,
    facts: asArray(rec.facts).filter((fact) => !isStaleSeasonText(fact, context)),
    risks: asArray(rec.risks).filter((risk) => !isStaleSeasonText(risk, context)),
  };
}

function visiblePreflightWarnings(preflight, selectedRecommendations) {
  const context = seasonChoiceContext(selectedRecommendations, preflight?.fact_pack || {});
  return asArray(preflight?.warnings).filter((warning) => !isStaleSeasonText(warning, context));
}

function visibleFactPackUnknowns(factPack, context) {
  return asArray(factPack?.unknowns).filter((item) => !isStaleSeasonText(item, context));
}

function visibleFactPackFacts(factPack, context) {
  return asArray(factPack?.facts).filter((fact) => !isStaleSeasonText(`${fact.statement} ${fact.category}`, context));
}

function fareMoney(value, currency = "INR") {
  const amount = Number(value || 0);
  if (!amount) return "Fare unavailable";
  return `${currency} ${amount.toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
}

function hotelBaseKey(base) {
  return `${base?.location || "stay"}-${asArray(base?.days).join("-")}`;
}

function applyHotelChoiceToPlan(plan, base, hotel) {
  if (!plan || !base || !hotel) return plan;
  const baseDays = new Set(asArray(base.days).map((day) => Number(day)));
  const existing = asArray(plan.accommodation).filter((item) => !baseDays.has(Number(item.day || 0)));
  const additions = asArray(base.days).map((day) => ({
    day: Number(day),
    type: "hotel",
    name: hotel.name,
    location: hotel.address || base.location,
    notes: [
      `Selected from hotel search results for ${base.location}.`,
      hotel.rating ? `Rating ${hotel.rating}/5` : "",
      hotel.reviews ? `${Number(hotel.reviews).toLocaleString("en-IN")} reviews` : "",
      hotel.price ? `Shown fare ${fareMoney(hotel.price, hotel.currency)}` : "",
      "Hotel price and availability can change until booking is confirmed.",
    ].filter(Boolean).join(" "),
  }));
  return { ...plan, accommodation: [...existing, ...additions].sort((left, right) => Number(left.day || 0) - Number(right.day || 0)) };
}

function buildEffectiveTripRequest(brief, foundation, answers, preflight, seasonContext) {
  if (!seasonContext?.shiftedToIdeal) return brief.trim();
  const factPack = preflight?.fact_pack || {};
  const intent = preflight?.intent || {};
  const lines = ["Create the trip from the user's final guided choices."];
  const destination = foundation.place || factPack.destination || intent.destination;
  const duration = intent.duration_days || answers?.duration_days;
  const groupSize = foundation.max_seats || intent.group_size || answers?.group_size;
  const budget = foundation.budget || intent.budget_per_person_inr || answers?.budget_per_person;
  const origin = answers?.origin || intent.origin;
  if (destination) lines.push(`Destination/route: ${destination}`);
  if (duration) lines.push(`Duration: ${duration} days`);
  if (origin) lines.push(`Origin: ${origin}`);
  if (groupSize) lines.push(`Group size: ${groupSize}`);
  if (budget) lines.push(`Budget per person: INR ${budget}`);
  lines.push(seasonContext.aiChoosesMonth
    ? `Final timing instruction: choose the single strongest exact month from ${seasonContext.idealPeriod}`
    : `Final chosen timing: ${seasonContext.idealPeriod}`);
  return lines.join("\n");
}

function preflightSteps(preflight, selectedRecommendations, completedStepKeys = [], answers = {}) {
  const completed = new Set(completedStepKeys || []);
  const context = seasonChoiceContext(selectedRecommendations, preflight?.fact_pack || {});
  const tags = selectedChoiceTags(selectedRecommendations, preflight?.recommendations || [], answers);
  const questionRank = {
    destination: 1, place: 1, region: 1, route: 1,
    duration_days: 2,
    dates_or_month: 3, month_or_season: 3, start_date: 3,
    origin: 4, start_location: 4,
    budget_per_person: 5, budget: 5, budget_per_person_inr: 5,
    group_size: 6, max_seats: 6,
    fitness_level: 12,
  };
  const recommendationRank = {
    season_timing: 7,
    places_route_scope: 8,
    budget_quality: 9,
    group_operations: 10,
    fitness_readiness: 11,
  };
  const questions = asArray(preflight?.missing_questions)
    .filter((question) => !completed.has(`question-${question.field}`))
    .filter((question) => !isBookingSelectionQuestion(question))
    .filter((question) => !isLowValuePlaceQuestion(question))
    .filter((question) => !(context.shiftedToIdeal && isSeasonTimingQuestion(question)))
    .filter((question) => !isStaleSeasonText(`${question.field} ${question.question} ${question.why}`, context))
    .map((question) => ({
      type: "question",
      key: `question-${question.field}`,
      rank: questionRank[question.field] || (question.priority === "critical" ? 20 : question.priority === "high" ? 25 : 40),
      question,
    }));
  const recommendations = buildRecommendationGroups(expandSeasonMonthRecommendations(preflight?.recommendations, preflight?.fact_pack)
    .filter((rec) => recommendationIsRelevant(rec, tags))
    .map((rec) => adaptRecommendationForSeason(rec, context))
    .filter(Boolean))
    .filter((group) => !completed.has(`recommendation-${group.key}`) && !isRecommendationGroupDone(group, selectedRecommendations))
    .map((group) => ({
      type: "recommendation",
      key: `recommendation-${group.key}`,
      rank: recommendationRank[group.key] || (group.items.some((item) => item.kind === "safety") ? 30 : 45),
      group,
    }));
  const hasTimingChoice = recommendations.some((item) => item.group.key === "season_timing");
  const visibleQuestions = hasTimingChoice
    ? questions.filter((item) => !isSeasonTimingQuestion(item.question))
    : questions;
  const remainingDecisionSlots = Math.max(0, 6 - completed.size);
  return [...visibleQuestions, ...recommendations]
    .sort((left, right) => left.rank - right.rank)
    .slice(0, remainingDecisionSlots);
}

function preflightAnswers(foundation, answers) {
  return {
    ...(answers || {}),
    ...(foundation.place ? { destination: foundation.place } : {}),
    ...(foundation.origin ? { origin: foundation.origin, start_location: foundation.origin } : {}),
    ...(foundation.start_date ? { start_date: foundation.start_date } : {}),
    ...(foundation.end_date ? { end_date: foundation.end_date } : {}),
    ...(foundation.budget ? { budget_per_person: foundation.budget } : {}),
    ...(foundation.max_seats ? { group_size: foundation.max_seats } : {}),
    ...(foundation.budget_scope ? { budget_scope: foundation.budget_scope } : {}),
    ...(foundation.stay_quality ? { stay_quality: foundation.stay_quality, accommodation_preference: foundation.stay_quality } : {}),
    ...(foundation.room_sharing ? { room_sharing: foundation.room_sharing } : {}),
    ...(foundation.comfort_level ? { comfort_level: foundation.comfort_level, pace: foundation.comfort_level } : {}),
  };
}

function missingRequiredFoundation(foundation) {
  return FOUNDATION_FIELDS.filter((field) => !String(foundation?.[field.key] || "").trim());
}

function buildGuidedBrief(brief, foundation, answers, selectedRecommendations, preflight) {
  const sections = [];
  const details = [];
  const factPack = preflight?.fact_pack || {};
  const seasonContext = seasonChoiceContext(selectedRecommendations, factPack);
  const intent = preflight?.intent || {};
  if (foundation.place) details.push(`Place/route: ${foundation.place}`);
  if (foundation.origin) details.push(`Starting city: ${foundation.origin}`);
  if (seasonContext.shiftedToIdeal) {
    details.push(seasonContext.aiChoosesMonth
      ? `Final timing instruction: AI must choose one exact month from ${seasonContext.idealPeriod}`
      : `Final chosen timing: ${seasonContext.idealPeriod}`);
  }
  else if (foundation.start_date || foundation.end_date) details.push(`Dates: ${foundation.start_date || "not set"} to ${foundation.end_date || "not set"}`);
  if (foundation.budget) details.push(`Budget per person: INR ${foundation.budget}`);
  if (foundation.budget_scope) details.push(`Budget scope: ${foundation.budget_scope}`);
  if (foundation.max_seats) details.push(`Maximum seats: ${foundation.max_seats}`);
  if (foundation.stay_quality) details.push(`Stay quality: ${foundation.stay_quality}`);
  if (foundation.room_sharing) details.push(`Room sharing: ${foundation.room_sharing}`);
  if (foundation.comfort_level) details.push(`Trip comfort level: ${foundation.comfort_level}`);
  if (details.length) sections.push(`FOUNDATIONAL TRIP DETAILS:\n${details.join("\n")}`);

  const answered = Object.entries(answers || {})
    .filter(([, value]) => String(value || "").trim())
    .filter(([key, value]) => !(seasonContext.shiftedToIdeal && isSeasonTimingQuestion({ field: key, question: String(value || ""), why: "" })))
    .map(([key, value]) => `${key.replace(/_/g, " ")}: ${value}`);
  if (answered.length) sections.push(`USER CLARIFICATIONS:\n${answered.join("\n")}`);
  if (selectedRecommendations.length) sections.push(`PLANNER-APPROVED FACTUAL GUIDANCE:\n${selectedRecommendations.join("\n")}`);
  const factsForBrief = seasonContext.shiftedToIdeal ? visibleFactPackFacts(factPack, seasonContext) : asArray(factPack.facts);
  const unknownsForBrief = seasonContext.shiftedToIdeal ? visibleFactPackUnknowns(factPack, seasonContext) : asArray(factPack.unknowns);
  const factLines = factsForBrief
    .map((fact) => {
      const needsCheck = fact.needs_live_check ? " [date-sensitive]" : "";
      const source = fact.source_title ? ` Source: ${fact.source_title}${fact.source_url ? ` (${fact.source_url})` : ""}` : "";
      return `${fact.category || "fact"}: ${fact.statement}${needsCheck}.${source}`;
    })
    .filter(Boolean);
  if (factPack.ideal_period || asArray(factPack.popular_places).length || factLines.length) {
    const factSummary = [];
    if (factPack.destination) factSummary.push(`Destination: ${factPack.destination}`);
    if (factPack.region) factSummary.push(`Region: ${factPack.region}`);
    if (factPack.requested_period && !seasonContext.shiftedToIdeal) factSummary.push(`User requested timing: ${factPack.requested_period}`);
    if (factPack.ideal_period) factSummary.push(`Ideal/advised timing: ${factPack.ideal_period}`);
    if (asArray(factPack.best_months).length) factSummary.push(`Best months to consider: ${factPack.best_months.join(", ")}`);
    asArray(factPack.month_experiences).forEach((item) => {
      if (!item?.month || !item?.experience) return;
      const pros = asArray(item.pros).filter(Boolean).join("; ");
      const cons = asArray(item.cons).filter(Boolean).join("; ");
      factSummary.push(`Experience in ${item.month}${item.verdict ? ` (${item.verdict})` : ""}: ${item.experience}${pros ? ` Pros: ${pros}.` : ""}${cons ? ` Cons: ${cons}.` : ""}`);
    });
    if (asArray(factPack.gateway_airports).length) factSummary.push(`Practical flight gateways: ${factPack.gateway_airports.join(", ")}`);
    if (asArray(factPack.popular_places).length) factSummary.push(`Popular places: ${factPack.popular_places.join(", ")}`);
    if (asArray(factPack.popular_routes).length) factSummary.push(`Popular/common routes: ${factPack.popular_routes.join(" | ")}`);
    sections.push(`FACT PACK TO RESPECT:\n${[...factSummary, ...factLines].join("\n")}`);
  }
  if (unknownsForBrief.length) {
    sections.push(`ITEMS TO CONFIRM BEFORE OPERATING:\n${unknownsForBrief.join("\n")}`);
  }
  sections.push("FLIGHT AND HOTEL SEARCH NOTE:\nCreate the itinerary from the chosen trip preferences and experience timing. After the draft route exists, TrailPulse will use its flight-deal API to apply the cheapest suitable exact date window and its hotel search API to apply the best-ranked stay for each route base. The planner may override those API selections later. Do not invent live flight or hotel prices in the itinerary JSON.");
  if (seasonContext.shiftedToIdeal) {
    const timingLock = seasonContext.aiChoosesMonth
      ? `Choose one strongest exact month from ${seasonContext.idealPeriod}, state that month clearly, and build the entire trip around it.`
      : `Use ${seasonContext.idealPeriod} as the only planning timing.`;
    sections.push(`FINAL CHOICE LOCKS:\n${timingLock}\nDo not mention, warn about, cost around, schedule around, or create infeasibility flags for any earlier timing/month from the first user sentence.`);
  }
  const assumptions = [...(preflight?.assumptions || [])];
  const origin = answers?.origin || answers?.start_location;
  if (origin && !answers?.end_destination && !answers?.return_location) {
    assumptions.push(`Assume the trip returns to ${origin} unless a different end point is specified.`);
  }
  if (assumptions.length) sections.push(`ASSUMPTIONS TO USE UNLESS CONTRADICTED:\n${Array.from(new Set(assumptions)).join("\n")}`);
  const base = buildEffectiveTripRequest(brief, foundation, answers, { ...preflight, intent }, seasonContext);
  return sections.length ? `${base.trim()}\n\n${sections.join("\n\n")}` : base.trim();
}

function updatePlanField(plan, key, value) {
  if (key === "group_size") {
    const groupSize = Number(value) || 1;
    const perPerson = Number(plan.cost_estimate?.per_person_minor || plan.budget_per_person?.amount_minor || 0);
    return {
      ...plan,
      group_size: groupSize,
      cost_estimate: { ...(plan.cost_estimate || {}), total_minor: perPerson * groupSize },
    };
  }
  if (key === "budget") {
    const amountMinor = Math.max(0, Number(value) || 0) * 100;
    const groupSize = Number(plan.group_size) || 1;
    return {
      ...plan,
      budget_per_person: { ...(plan.budget_per_person || {}), amount_minor: amountMinor, currency: "INR", kind: "estimate" },
      cost_estimate: {
        ...(plan.cost_estimate || {}),
        per_person_minor: amountMinor,
        total_minor: amountMinor * groupSize,
        currency: "INR",
        authoritative: false,
      },
    };
  }
  return { ...plan, [key]: value };
}

function Section({ icon: Icon, title, children, action }) {
  return (
    <div className="tp-panel p-5">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <h3 className="flex items-center gap-2 text-sm font-black text-[#17211D]"><Icon className="h-4 w-4 text-[#2D7A65]" /> {title}</h3>
        {action}
      </div>
      {children}
    </div>
  );
}

function InfoMetric({ label, value, tone = "neutral" }) {
  const toneCls = tone === "warn"
    ? "border-amber-200 bg-amber-50 text-amber-900"
    : tone === "good"
      ? "border-[#BFE2D4] bg-[#F4FBF7] text-[#254F43]"
      : "border-[#E2DFD7] bg-[#FFFCF7] text-[#17211D]";
  return (
    <div className={`rounded-lg border px-3 py-2 ${toneCls}`}>
      <p className="text-[10px] font-black uppercase tracking-[0.14em] opacity-70">{label}</p>
      <p className="mt-1 text-sm font-black">{value || "Pending"}</p>
    </div>
  );
}

function AttentionItem({ tone = "warn", title, children }) {
  const styles = tone === "danger"
    ? "border-rose-200 bg-rose-50 text-rose-800"
    : "border-amber-200 bg-amber-50 text-amber-800";
  return (
    <div className={`rounded-lg border p-3 ${styles}`}>
      <p className="flex items-center gap-1.5 text-xs font-black"><AlertTriangle className="h-3.5 w-3.5" /> {title}</p>
      <div className="mt-1 text-xs leading-5">{children}</div>
    </div>
  );
}

function compactList(items, limit = 3) {
  const clean = asArray(items).filter(Boolean);
  if (clean.length <= limit) return clean.join(", ");
  return `${clean.slice(0, limit).join(", ")} +${clean.length - limit} more`;
}

function consultantOptionLabel(label) {
  const text = String(label || "").toLowerCase();
  if (text.includes("user") || text.includes("request")) return "What you asked for";
  if (text.includes("ai") || text.includes("advised") || text.includes("recommended")) return "My recommendation";
  if (text.includes("common")) return "What most people try";
  if (text.includes("flexible")) return "Keep it flexible";
  if (text.includes("ideal")) return "Best experience";
  if (text.includes("popular")) return "Popular choice";
  if (text.includes("safer")) return "Safer choice";
  return label || "Option";
}

function consultantTitle(title) {
  return String(title || "")
    .replace(/^Keep the user's /i, "Keep your ")
    .replace(/^Use a /i, "Choose a ")
    .replace(/^Add /i, "Include ")
    .replace(/^Run /i, "Plan ")
    .replace(/\buser's\b/gi, "your")
    .replace(/\buser\b/gi, "traveler");
}

function consultantText(text) {
  return String(text || "")
    .replace(/\bMust verify live:\s*/gi, "I evaluated: ")
    .replace(/\bMust verify:\s*/gi, "I evaluated: ")
    .replace(/\bConfirm before operating:\s*/gi, "Planning note: ")
    .replace(/\bmust be checked from current sources\b/gi, "can change by date")
    .replace(/\bshould be checked from current sources\b/gi, "can change by date")
    .replace(/\bmust be checked\b/gi, "can change")
    .replace(/\bshould be checked\b/gi, "can change")
    .replace(/\bmust still be checked\b/gi, "can still change")
    .replace(/\bstill needs? (?:flight, hotel, and local-condition )?checks?\b/gi, "still depends on exact dates")
    .replace(/\bexact dates still need\b/gi, "exact dates affect")
    .replace(/\bconfirm(?:ed|ing)?\b/gi, "review")
    .replace(/\bverification tasks?\b/gi, "planning notes")
    .replace(/\bfact-check tasks?\b/gi, "planning notes")
    .replace(/\bsource checks?\b/gi, "research")
    .replace(/\bcurrent checks?\b/gi, "current research")
    .replace(/\blive checks?\b/gi, "current checks")
    .replace(/\blive details\b/gi, "current details")
    .replace(/\blive prices\b/gi, "current prices")
    .replace(/\blive rates\b/gi, "current rates")
    .replace(/\bthe user's\b/gi, "your")
    .replace(/\bthe user\b/gi, "you")
    .replace(/\buser's\b/gi, "your")
    .replace(/\buser\b/gi, "traveler")
    .replace(/\buser-provided\b/gi, "your")
    .replace(/\buser-selected\b/gi, "your selected")
    .replace(/\bmust-haves\b/gi, "must-have places")
    .replace(/\bvalidate whether\b/gi, "check if")
    .replace(/\bvalidate\b/gi, "check")
    .replace(/\bfeasibility\b/gi, "fit")
    .replace(/\boperational reliability\b/gi, "smooth execution")
    .replace(/\boperations\b/gi, "on-ground planning")
    .replace(/\blogistics\b/gi, "travel arrangements")
    .replace(/\boperating conditions\b/gi, "local conditions")
    .replace(/\bavailable days\b/gi, "days you have")
    .replace(/\btransport\b/gi, "travel time")
    .replace(/\baccess\b/gi, "entry/access")
    .replace(/\bseason\b/gi, "travel season")
    .replace(/\bconstraints\b/gi, "limits")
    .replace(/\binventory\b/gi, "hotel and vehicle availability")
    .replace(/\bcontingency\b/gi, "backup")
    .replace(/\bbuffer\b/gi, "extra time");
}

function plainConsultantText(text, maxLength = 180) {
  const cleaned = consultantText(text)
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, "$1")
    .replace(/https?:\/\/\S+/g, "")
    .replace(/\s+/g, " ")
    .trim();
  if (!maxLength || cleaned.length <= maxLength) return cleaned;
  return `${cleaned.slice(0, maxLength).replace(/\s+\S*$/, "")}...`;
}

function simpleQuestionText(text) {
  return plainConsultantText(text, 180)
    .replace(/\bDoes the INR ([\d,]+) per person include return flights\b/i, "Does the INR $1 per person include return flights")
    .replace(/\bor is it ground-only\?/i, "or only the land package after arrival?")
    .replace(/\bex-Guwahati\/Shillong\b/gi, "after reaching Guwahati or Shillong")
    .replace(/\bwhich experiences are must-have\b/i, "Which experiences matter most")
    .replace(/\bwhich.*should.*target\b/i, "What should we plan around")
    .replace(/\bshould this trip use\b/i, "should we use")
    .replace(/\bWhere will the group start from\b/i, "Where will the group start")
    .replace(/\bWhat budget per person should we target\b/i, "What budget should we keep per person")
    .replace(/\bHow many travelers are expected\b/i, "How many people are travelling")
    .replace(/\bWhen are you planning to travel\b/i, "When do you want to travel");
}

function simpleWhyText(text) {
  return plainConsultantText(text, 170)
    .replace(/\bThis one factor sets stay quality, vehicle choice, and whether a third base is feasible\./i, "This helps me choose the right hotels, vehicles, and route.")
    .replace(/\bdrives feasibility\b/gi, "affects the plan")
    .replace(/\bmajor quality and fatigue factors\b/gi, "important for comfort and travel time")
    .replace(/\boperational\b/gi, "on-ground")
    .replace(/\bconstraints\b/gi, "limits")
    .replace(/\bfeasible\b/gi, "realistic");
}

function titleCaseLabel(value) {
  return String(value || "Choice")
    .replace(/_/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function decisionLabelForField(field) {
  const key = String(field || "").toLowerCase();
  const labels = {
    origin: "Origin",
    start_location: "Origin",
    destination: "Place",
    place: "Place",
    route: "Route",
    dates_or_month: "Timing",
    month_or_season: "Timing",
    start_date: "Start date",
    end_date: "End date",
    budget_per_person: "Budget",
    budget_per_person_inr: "Budget",
    budget: "Budget",
    budget_scope: "Budget scope",
    budget_inclusions: "Budget scope",
    group_size: "Group size",
    max_seats: "Group size",
    fitness_level: "Fitness",
    accommodation_preference: "Stays",
    transport_preference: "Transport",
    must_have: "Must-have places",
    must_haves: "Must-have places",
    places_to_visit: "Must-have places",
  };
  return labels[key] || titleCaseLabel(field);
}

function decisionLabelForGroup(group, fallback) {
  const labels = {
    season_timing: "Timing",
    places_route_scope: "Route",
    budget_quality: "Budget",
    group_operations: "Group operations",
    fitness_readiness: "Fitness",
    safety_cautions: "Safety",
    access_permits: "Access",
    transport_choice: "Transport",
    accommodation_choice: "Stays",
  };
  return labels[String(group || "").toLowerCase()] || decisionLabelForField(fallback || group || "Choice");
}

function simplifyDecisionValue(value) {
  return plainConsultantText(value, 110)
    .replace(/^What you asked for:\s*/i, "")
    .replace(/^My recommendation:\s*/i, "")
    .replace(/^Choose\s+/i, "")
    .replace(/^Use\s+/i, "")
    .replace(/^Keep\s+/i, "Keep ")
    .replace(/\bthe user's\b/gi, "your")
    .replace(/\buser's\b/gi, "your")
    .replace(/\buser\b/gi, "traveler");
}

function completedDecisionSummaries({ answers = {}, selectedRecommendations = [], recommendations = [], completedStepKeys = [] }) {
  const completed = new Set(completedStepKeys || []);
  const items = [];
  Object.entries(answers || {}).forEach(([key, value]) => {
    if (!String(value || "").trim()) return;
    if (completed.size && !completed.has(`question-${key}`)) return;
    items.push({
      key: `answer-${key}`,
      label: decisionLabelForField(key),
      value: simplifyDecisionValue(String(value)),
      detail: decisionDetailForAnswer(key, value),
      tone: "user",
    });
  });
  const selected = new Set(selectedRecommendations || []);
  asArray(recommendations).forEach((rec, index) => {
    const baseGuidance = rec.apply_text || rec.advice;
    const monthOptions = asArray(rec.month_options);
    const dynamicTimingGuidance = monthOptions.length > 0
      ? asArray(selectedRecommendations).find((item) => /Final timing choice: use AI-advised ideal timing/i.test(String(item)))
      : "";
    const guidance = selected.has(baseGuidance) ? baseGuidance : dynamicTimingGuidance;
    if (!guidance) return;
    const optionLabel = consultantOptionLabel(rec.option_label || "");
    const chosenMonth = monthOptions.find((month) => String(guidance).toLowerCase().includes(` in ${String(month).toLowerCase()}`));
    const dynamicTitle = chosenMonth
      ? `Recommended month: ${chosenMonth}`
      : monthOptions.length > 0 && /let (?:the )?ai choose/i.test(String(guidance))
        ? `AI chooses from ${monthOptions.join(", ")}`
        : rec.title;
    const title = simplifyDecisionValue(consultantTitle(dynamicTitle || guidance));
    items.push({
      key: `rec-${rec.option_group || index}-${index}`,
      label: decisionLabelForGroup(rec.option_group, rec.kind),
      value: optionLabel && optionLabel !== "Option" ? `${optionLabel}: ${title}` : title,
      detail: decisionDetailForRecommendation(rec),
      tone: optionLabel === "My recommendation" ? "advised" : "user",
    });
  });
  return items;
}

function decisionDetailForAnswer(key, value) {
  const label = decisionLabelForField(key).toLowerCase();
  const text = String(value || "").trim();
  if (!text) return "";
  if (label.includes("origin")) return "We will plan flights, arrival buffer, and return path from this city.";
  if (label.includes("budget")) return "This will guide hotel quality, vehicle choice, flights, and activity tradeoffs.";
  if (label.includes("group")) return "This affects vehicles, rooms, guides, meal timing, and movement speed.";
  if (label.includes("timing") || label.includes("date")) return "Weather, prices, crowds, and route safety will be checked around this timing.";
  if (label.includes("fitness")) return "This decides whether treks and long walks should stay in or become optional.";
  if (label.includes("stay")) return "This guides hotel category and overnight base choices.";
  if (label.includes("transport")) return "This affects travel comfort, access to narrow roads, and total cost.";
  if (label.includes("must-have")) return "I will protect these experiences first, then fit the route around the days available.";
  return "This choice will shape the next planning decision.";
}

function decisionDetailForRecommendation(rec = {}) {
  const parts = [
    ...asArray(rec.benefits),
    rec.operations_impact,
    rec.cost_impact,
    rec.evidence,
    rec.advice,
  ].filter(Boolean);
  return plainConsultantText(parts[0] || "", 150);
}

function DecisionSummaryBar({ items, compact = false }) {
  if (!items.length) return null;
  const visible = items.slice(compact ? -4 : -6);
  const hiddenCount = Math.max(0, items.length - visible.length);
  return (
    <div className="mt-3 rounded-xl border border-[#D8EAE2] bg-white/90 p-3 shadow-sm" data-testid="ai-decision-summary">
      <div className="flex flex-wrap items-end justify-between gap-2">
        <div>
          <p className="text-[10px] font-black uppercase tracking-[0.14em] text-[#2D7A65]">Your choices so far</p>
          {!compact && <p className="mt-0.5 text-[11px] leading-4 text-[#516259]">What the next decisions will build on.</p>}
        </div>
        {hiddenCount > 0 && <span className="rounded-full bg-[#F4FBF7] px-2 py-1 text-[10px] font-black text-[#254F43]">+{hiddenCount} earlier choice{hiddenCount === 1 ? "" : "s"}</span>}
      </div>
      <div className={`mt-2 grid gap-2 ${compact ? "sm:grid-cols-2" : "sm:grid-cols-2 lg:grid-cols-4"}`}>
        {visible.map((item) => (
          <div key={item.key} className={`rounded-lg border px-3 py-2 ${item.tone === "advised" ? "border-[#BFE2D4] bg-[#F4FBF7]" : "border-[#ECEAE4] bg-[#FFFCF7]"}`}>
            <div className="flex items-start gap-2">
              <span className={`mt-0.5 h-2.5 w-2.5 shrink-0 rounded-full ${item.tone === "advised" ? "bg-[#2D7A65]" : "bg-[#E5B85C]"}`} />
              <div className="min-w-0">
                <p className="text-[10px] font-black uppercase tracking-[0.12em] text-[#516259]">{item.label}</p>
                <p className="mt-0.5 text-xs font-black leading-4 text-[#17211D]">{plainConsultantText(item.value, compact ? 78 : 88)}</p>
                {item.detail && (
                  <p className="mt-0.5 text-[11px] font-semibold leading-4 text-[#516259]">{plainConsultantText(item.detail, compact ? 92 : 100)}</p>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function CompactResearchStrip({ preflight, seasonContext }) {
  const factPack = preflight?.fact_pack || {};
  const intelligence = preflight?.destination_intelligence || {};
  const advised = intelligence.advised_version || {};
  const facts = asArray(intelligence.fact_based);
  const trends = asArray(intelligence.trend_based);
  const factUnknowns = visibleFactPackUnknowns(factPack, seasonContext);
  const items = [
    factPack.ideal_period || advised.timing ? { label: "Best time", value: factPack.ideal_period || advised.timing } : null,
    asArray(factPack.popular_places).length ? { label: "Popular", value: factPack.popular_places.slice(0, 4).join(", ") } : null,
    asArray(factPack.gateway_airports).length ? { label: "Airports", value: factPack.gateway_airports.slice(0, 3).join(", ") } : null,
    facts[0] ? { label: "Fact", value: facts[0] } : null,
    trends[0] ? { label: "Trend", value: trends[0] } : null,
    factUnknowns[0] ? { label: "Confirm", value: factUnknowns[0] } : null,
  ].filter(Boolean).slice(0, 4);
  if (!items.length) return null;
  return (
    <div className="mt-3 rounded-xl border border-[#CFE8DD] bg-[#F4FBF7] p-3" data-testid="ai-compact-research">
      <p className="text-[10px] font-black uppercase tracking-[0.14em] text-[#2D7A65]">Research context</p>
      <div className="mt-2 grid gap-2 sm:grid-cols-2">
        {items.map((item) => (
          <p key={`${item.label}-${item.value}`} className="rounded-lg border border-[#D8EEE5] bg-white px-3 py-2 text-xs leading-5 text-[#516259]">
            <span className="font-black text-[#17211D]">{item.label}:</span> {plainConsultantText(item.value, 120)}
          </p>
        ))}
      </div>
    </div>
  );
}

function evidenceBadgesFor(rec = {}) {
  const typed = String(rec.evidence_type || "").toLowerCase();
  if (typed === "fact") return [{ label: "Fact-based", cls: "border-[#BFE2D4] bg-[#F4FBF7] text-[#254F43]" }];
  if (typed === "trend") return [{ label: "Trend-based", cls: "border-sky-200 bg-sky-50 text-sky-800" }];
  if (typed === "mixed") {
    return [
      { label: "Fact + trend", cls: "border-[#BFE2D4] bg-[#F4FBF7] text-[#254F43]" },
    ];
  }
  const text = [
    rec.evidence,
    rec.advice,
    rec.apply_text,
    ...asArray(rec.facts),
    ...asArray(rec.source_links).map(() => "source-backed"),
  ].join(" ").toLowerCase();
  const badges = [];
  if (asArray(rec.source_links).length > 0 || text.includes("fact-based") || text.includes("source-backed")) {
    badges.push({ label: "Fact-based", cls: "border-[#BFE2D4] bg-[#F4FBF7] text-[#254F43]" });
  }
  if (text.includes("trend-based") || text.includes("popular") || text.includes("common traveler") || text.includes("operator pattern")) {
    badges.push({ label: "Trend-based", cls: "border-sky-200 bg-sky-50 text-sky-800" });
  }
  return badges;
}

function rememberActiveGeneration(id) {
  if (!id || typeof window === "undefined") return;
  window.localStorage.setItem(ACTIVE_AI_GENERATION_KEY, id);
}

function forgetActiveGeneration(id) {
  if (typeof window === "undefined") return;
  if (!id || window.localStorage.getItem(ACTIVE_AI_GENERATION_KEY) === id) {
    window.localStorage.removeItem(ACTIVE_AI_GENERATION_KEY);
  }
}

function rememberedActiveGeneration() {
  if (typeof window === "undefined") return "";
  return window.localStorage.getItem(ACTIVE_AI_GENERATION_KEY) || "";
}

function rememberPlannerDraftState(state) {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(AI_PLANNER_DRAFT_KEY, JSON.stringify({ ...state, savedAt: new Date().toISOString() }));
}

function rememberedPlannerDraftState() {
  if (typeof window === "undefined") return {};
  try {
    return JSON.parse(window.localStorage.getItem(AI_PLANNER_DRAFT_KEY) || "{}") || {};
  } catch {
    return {};
  }
}

function forgetPlannerDraftState() {
  if (typeof window === "undefined") return;
  window.localStorage.removeItem(AI_PLANNER_DRAFT_KEY);
}

function SavedPlansPanel({ plans, loading, onOpen }) {
  return (
    <div className="tp-panel mb-5 p-4" data-testid="ai-saved-plans">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div>
          <p className="text-sm font-black text-[#17211D]">Saved AI plans</p>
          <p className="text-xs text-[#516259]">{loading ? "Loading..." : `${plans.length} saved`}</p>
        </div>
      </div>
      {plans.length === 0 ? (
        <div className="rounded-xl border border-dashed border-[#D8D1C2] bg-[#FFFCF7] p-4 text-sm text-[#516259]">
          No saved AI plans yet.
        </div>
      ) : (
        <div className="grid gap-3 md:grid-cols-2">
          {plans.map((item) => (
            <div key={item.id} className="rounded-xl border border-[#E2DFD7] bg-[#FFFCF7] p-3">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="truncate text-sm font-black text-[#17211D]">{item.name || item.title}</p>
                  <p className="mt-1 text-xs text-[#516259]">
                    {[item.region, item.duration_days ? `${item.duration_days} days` : "", item.group_size ? `${item.group_size} seats` : ""].filter(Boolean).join(" - ")}
                  </p>
                  <p className="mt-1 text-xs text-slate-400">
                    {item.budget_per_person_minor ? `${inr(item.budget_per_person_minor)} / person` : "Cost estimate pending"}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => onOpen(item.id)}
                  className="inline-flex min-h-9 items-center gap-1.5 rounded-md border border-[#CFE8DD] bg-white px-3 text-xs font-bold text-[#254F43] hover:border-[#2D7A65]"
                >
                  <Eye className="h-3.5 w-3.5" /> Open
                </button>
              </div>
              {item.accepted_trip_id && <p className="mt-2 text-[11px] font-bold text-[#2D7A65]">Trip created</p>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function CompactGenerationSummary({ brief, foundation, answers, selectedRecommendations, preflight, mode }) {
  const factPack = preflight?.fact_pack || {};
  const decisionItems = completedDecisionSummaries({
    answers,
    selectedRecommendations,
    recommendations: expandSeasonMonthRecommendations(preflight?.recommendations || [], factPack),
  });
  const checking = mode === "preflight";
  const creating = mode === "generation";
  const title = checking ? "Trip inputs received" : creating ? "Trip inputs locked for draft" : "Trip inputs summarized";
  const badge = checking ? "Checking idea" : creating ? "Creating draft" : "Choices ready";
  const summaryItems = [
    foundation.place || factPack.destination ? { label: "Place", value: foundation.place || factPack.destination } : null,
    foundation.origin ? { label: "Start", value: foundation.origin } : null,
    foundation.start_date || foundation.end_date ? { label: "Dates", value: `${foundation.start_date || "Not fixed"} to ${foundation.end_date || "Not fixed"}` } : null,
    foundation.budget ? { label: "Budget", value: `INR ${foundation.budget} / person` } : null,
    foundation.budget_scope ? { label: "Scope", value: foundation.budget_scope } : null,
    foundation.max_seats ? { label: "Group", value: `${foundation.max_seats} seats` } : null,
    foundation.stay_quality ? { label: "Stay", value: foundation.stay_quality } : null,
    foundation.room_sharing ? { label: "Rooms", value: foundation.room_sharing } : null,
    foundation.comfort_level ? { label: "Comfort", value: foundation.comfort_level } : null,
    asArray(factPack.best_months).length
      ? { label: "Strongest months", value: factPack.best_months.slice(0, 3).join(", ") }
      : factPack.ideal_period ? { label: "Ideal time", value: factPack.ideal_period } : null,
  ].filter(Boolean);
  return (
    <div className="rounded-2xl border border-[#E2DFD7] bg-[#FFFCF7] p-4" data-testid="ai-generation-summary">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-sm font-black text-[#17211D]">{creating ? title : "Plan so far"}</p>
          <p className="mt-1 max-w-3xl text-xs leading-5 text-[#516259]">{brief}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="rounded-full border border-[#CFE8DD] bg-[#F4FBF7] px-3 py-1.5 text-xs font-black text-[#254F43]">
            {badge}
          </div>
        </div>
      </div>
      {summaryItems.length > 0 && (
        <div className="mt-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-5">
          {summaryItems.map((item) => (
            <div key={item.label} className="rounded-xl border border-[#ECEAE4] bg-white px-3 py-2">
              <p className="text-[10px] font-black uppercase tracking-[0.12em] text-[#8B9A92]">{item.label}</p>
              <p className="mt-1 truncate text-xs font-bold text-[#17211D]">{item.value}</p>
            </div>
          ))}
        </div>
      )}
      <DecisionSummaryBar items={decisionItems} />
    </div>
  );
}

function normalizeQuestionChoice(choice, question = {}) {
  if (typeof choice === "string") {
    return {
      label: choice,
      value: choice,
      detail: simpleWhyText(question.why || "This choice will shape the next part of the trip."),
      pros: [],
      cons: [],
    };
  }
  const label = String(choice?.label || choice?.value || "").trim();
  return {
    label,
    value: String(choice?.value || label).trim(),
    detail: String(choice?.detail || "").trim(),
    pros: asArray(choice?.pros).filter(Boolean),
    cons: asArray(choice?.cons).filter(Boolean),
  };
}

function QuestionChoiceCards({ question, value, onChange, onCommit, multiple = false }) {
  const choices = asArray(question.choices)
    .map((choice) => normalizeQuestionChoice(choice, question))
    .filter((choice) => choice.label && choice.value);
  const selectedValues = multiple
    ? String(value || "").split(",").map((item) => item.trim()).filter(Boolean)
    : [String(value || "").trim()].filter(Boolean);
  const choose = (choiceValue) => {
    if (!multiple) {
      onChange(choiceValue);
      onCommit?.(choiceValue);
      return;
    }
    const next = selectedValues.includes(choiceValue)
      ? selectedValues.filter((item) => item !== choiceValue)
      : [...selectedValues, choiceValue];
    onChange(next.join(", "));
  };

  return (
    <div className="grid gap-3 md:grid-cols-2">
      {choices.map((choice) => {
        const selected = selectedValues.includes(choice.value);
        const hasDetails = choice.pros.length > 0 || choice.cons.length > 0;
        return (
          <div
            key={choice.value}
            className={`rounded-lg border p-3 transition ${selected ? "border-[#2D7A65] bg-[#F4FBF7]" : "border-[#ECEAE4] bg-[#FFFCF7] hover:border-[#BFE2D4]"}`}
          >
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <p className="text-sm font-black leading-5 text-[#17211D]">{choice.label}</p>
                {choice.detail && <p className="mt-1 text-xs leading-5 text-[#516259]">{plainConsultantText(choice.detail, 230)}</p>}
              </div>
              <button
                type="button"
                onClick={() => choose(choice.value)}
                aria-pressed={selected}
                className={`inline-flex min-h-9 shrink-0 items-center gap-2 rounded-md border px-3 text-xs font-bold ${selected ? "border-[#2D7A65] bg-[#2D7A65] text-white" : "border-[#CFE8DD] bg-white text-[#254F43] hover:border-[#2D7A65]"}`}
              >
                <Check className="h-3.5 w-3.5" /> {selected ? "Chosen" : "Choose this"}
              </button>
            </div>
            {hasDetails && (
              <details className="mt-2 rounded-md border border-[#ECEAE4] bg-white/70 p-2">
                <summary className="cursor-pointer text-[11px] font-black uppercase tracking-[0.1em] text-[#516259]">Details</summary>
                <div className="mt-2 grid gap-2 text-[11px] leading-4 sm:grid-cols-2">
                  {choice.pros.length > 0 && (
                    <div className="rounded-md bg-[#F4FBF7] p-2 text-[#254F43]">
                      <p className="font-black">Good for you</p>
                      <p>{choice.pros.slice(0, 3).map((item) => plainConsultantText(item, 100)).join(" | ")}</p>
                    </div>
                  )}
                  {choice.cons.length > 0 && (
                    <div className="rounded-md bg-amber-50 p-2 text-amber-900">
                      <p className="font-black">Things to know</p>
                      <p>{choice.cons.slice(0, 3).map((item) => plainConsultantText(item, 100)).join(" | ")}</p>
                    </div>
                  )}
                </div>
              </details>
            )}
          </div>
        );
      })}
    </div>
  );
}

function QuestionInput({ question, value, onChange, onCommit, onFocus, onBlur }) {
  const questionText = `${question.field} ${question.question}`.toLowerCase();
  const isPlaceQuestion = /place|places|visit|route|destination|region/i.test(questionText);
  const isMultiChoiceQuestion = /(must[-\s]?have|experience|experiences|interests|activities|activity|cover|visit|places|attractions)/i.test(questionText);
  const canLetAiSuggest = isPlaceExplorationQuestion(question);
  const hasChoices = asArray(question.choices).length > 0;
  if (hasChoices) {
    const multiple = isPlaceQuestion || isMultiChoiceQuestion;
    return (
      <div className="space-y-2">
        <QuestionChoiceCards question={question} value={value} onChange={onChange} onCommit={onCommit} multiple={multiple} />
        {multiple && (
          <input
            className={inputCls}
            value={value || ""}
            onChange={(event) => onChange(event.target.value)}
            onFocus={onFocus}
            onBlur={onBlur}
            placeholder={isPlaceQuestion ? "Choose cards or type places" : "Choose cards or type your own"}
          />
        )}
        {multiple && (
          <p className="text-[11px] leading-4 text-[#516259]">
            {canLetAiSuggest ? "Choose one or more, type your own, or let AI suggest the best fit." : "You can choose more than one."}
          </p>
        )}
      </div>
    );
  }
  return (
    <input
      className={inputCls}
      type={question.answer_type === "number" ? "number" : question.answer_type === "date" ? "date" : "text"}
      min={question.answer_type === "number" ? "1" : undefined}
      value={value || ""}
      onChange={(event) => onChange(event.target.value)}
      onFocus={onFocus}
      onBlur={onBlur}
      placeholder="Answer here"
    />
  );
}

function PreflightPanel({ preflight, answers, setAnswers, selectedRecommendations, completedStepKeys, setCompletedStepKeys, onAnswerStep, onRecommendationStep, refreshing }) {
  if (!preflight) return null;
  const steps = preflightSteps(preflight, selectedRecommendations, completedStepKeys, answers);
  const activeStep = steps[0];
  const totalSteps = completedStepKeys.length + steps.length;
  const answeredSteps = completedStepKeys.length;
  const stepNumber = activeStep ? answeredSteps + 1 : totalSteps;
  const remainingAfterThis = Math.max(0, steps.length - 1);
  const markStepDone = (key) => {
    setCompletedStepKeys((prev) => (prev.includes(key) ? prev : [...prev, key]));
  };
  const unansweredQuestions = activeStep?.type === "question" ? [activeStep.question] : [];
  const recommendationGroups = activeStep?.type === "recommendation" ? [activeStep.group] : [];
  return (
    <div className="mt-5 space-y-4" data-testid="ai-preflight-panel">
      <div className="rounded-2xl border border-[#E2DFD7] bg-[#FFFCF7] p-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <p className="flex items-center gap-2 text-sm font-black text-[#17211D]"><HelpCircle className="h-4 w-4 text-[#2D7A65]" /> Trip decisions</p>
            <p className="mt-1 text-xs leading-5 text-[#516259]">Answer the next useful thing. I will use your earlier choices to keep the plan focused.</p>
          </div>
          <div className="rounded-xl border border-[#E2DFD7] bg-white px-3 py-2 text-right">
            <p className="text-[10px] font-black uppercase tracking-[0.14em] text-[#516259]">Progress</p>
            <p className="text-sm font-black text-[#17211D]">{steps.length ? `Step ${stepNumber} of ${steps.length}` : "Ready"}</p>
          </div>
        </div>
        {preflight.warnings?.length > 0 && (
          <div className="mt-3 rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs font-semibold leading-5 text-amber-800">
            {preflight.warnings.slice(0, 2).join(" ")}
          </div>
        )}
        {activeStep && (
          <p className="mt-3 text-xs font-semibold text-[#516259]">
            {remainingAfterThis} more estimated step{remainingAfterThis === 1 ? "" : "s"} after this.
          </p>
        )}
      </div>

      {unansweredQuestions.length > 0 && (
        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4" data-testid="ai-followup-questions">
          <p className="flex items-center gap-2 text-sm font-black text-amber-950"><HelpCircle className="h-4 w-4" /> Quick question</p>
          <div className="mt-3 grid gap-3 sm:grid-cols-2">
            {unansweredQuestions.map((question) => (
              <div key={question.field} className="rounded-xl border border-amber-200 bg-white/70 p-3">
                <span className="mb-1 block text-sm font-black text-[#17211D]">{simpleQuestionText(question.question)}</span>
                {question.why && <span className="mb-2 block text-xs leading-5 text-[#516259]">{simpleWhyText(question.why)}</span>}
                <QuestionInput
                  question={question}
                  value={answers[question.field]}
                  onChange={(value) => setAnswers((prev) => ({ ...prev, [question.field]: value }))}
                />
                <div className="mt-3 flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => onAnswerStep(question)}
                    disabled={refreshing || (!isQuestionAnswered(question, answers) && !isPlaceExplorationQuestion(question))}
                    className="inline-flex min-h-9 items-center gap-2 rounded-md border border-[#2D7A65] bg-[#2D7A65] px-3 text-xs font-bold text-white disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {refreshing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />} Next
                  </button>
                  {isPlaceExplorationQuestion(question) && (
                    <button type="button" onClick={() => markStepDone(`question-${question.field}`)} className="inline-flex min-h-9 items-center rounded-md border border-amber-200 bg-white px-3 text-xs font-bold text-amber-900">
                      Let AI suggest
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {recommendationGroups.length > 0 && (
      <div className="rounded-2xl border border-[#CFE8DD] bg-[#F4FBF7] p-4" data-testid="ai-recommendations">
        <p className="flex items-center gap-2 text-sm font-black text-[#17211D]"><Sparkles className="h-4 w-4 text-[#2D7A65]" /> Choose your preference</p>
        <div className="mt-3 grid gap-3">
          {recommendationGroups.map((group) => {
            const grouped = group.items.length > 1;
            const alternatives = group.items.map((item) => item.apply_text || item.advice);
            return (
              <div key={group.key} className="rounded-xl border border-[#CFE8DD] bg-white p-3">
                {grouped && <p className="mb-2 text-xs font-bold text-[#2D7A65]">Pick one direction. The next step will follow this choice.</p>}
                <div className={`grid gap-3 ${grouped ? "md:grid-cols-2" : ""}`}>
                  {group.items.map((rec) => {
                    const guidance = rec.apply_text || rec.advice;
                    const selected = selectedRecommendations.includes(guidance);
                    const evidenceBadges = evidenceBadgesFor(rec);
                    return (
                      <div key={`${rec.title}-${rec.originalIndex}`} className={`rounded-lg border p-3 ${selected ? "border-[#2D7A65] bg-[#F4FBF7]" : "border-[#ECEAE4] bg-[#FFFCF7]"}`}>
                        <div className="flex flex-wrap items-start justify-between gap-3">
                          <div className="min-w-0 flex-1">
                            {rec.option_label && <p className="mb-1 text-[11px] font-black uppercase tracking-[0.12em] text-[#516259]">{consultantOptionLabel(rec.option_label)}</p>}
                            <p className="text-sm font-black text-[#17211D]">{consultantTitle(rec.title)}</p>
                            {evidenceBadges.length > 0 && (
                              <div className="mt-2 flex flex-wrap gap-1.5">
                                {evidenceBadges.map((badge) => (
                                  <span key={badge.label} className={`rounded-full border px-2 py-0.5 text-[10px] font-black uppercase tracking-[0.08em] ${badge.cls}`}>{badge.label}</span>
                                ))}
                              </div>
                            )}
                            <p className="mt-1 text-xs leading-5 text-[#516259]">{consultantText(rec.advice)}</p>
                            {rec.evidence && <p className="mt-1 text-[11px] font-semibold leading-5 text-[#2D7A65]">Why I'm saying this: {consultantText(rec.evidence)}</p>}
                          </div>
                          <button
                            type="button"
                            onClick={() => onRecommendationStep(guidance, alternatives)}
                            disabled={refreshing}
                            className={`inline-flex min-h-9 items-center gap-2 rounded-md border px-3 text-xs font-bold ${selected ? "border-[#2D7A65] bg-[#2D7A65] text-white" : "border-[#CFE8DD] bg-white text-[#254F43] hover:border-[#2D7A65]"}`}
                          >
                            {refreshing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />} {selected ? "Chosen" : grouped ? "Choose this" : "Use this"}
                          </button>
                        </div>
                      </div>
                    );
                  })}
                  </div>
              </div>
            );
          })}
        </div>
      </div>
      )}
      {!activeStep && (
        <div className="rounded-2xl border border-[#CFE8DD] bg-[#F4FBF7] p-4" data-testid="ai-guided-complete">
          <p className="flex items-center gap-2 text-sm font-black text-[#17211D]"><Check className="h-4 w-4 text-[#2D7A65]" /> Ready to create the draft</p>
          <p className="mt-1 text-xs leading-5 text-[#516259]">The key questions and planning choices are handled. You can create the trip draft now.</p>
        </div>
      )}
    </div>
  );
}

function DestinationIntelligencePanel({ intelligence }) {
  if (!intelligence) return null;
  const requested = intelligence.requested_version || {};
  const advised = intelligence.advised_version || {};
  const facts = asArray(intelligence.fact_based);
  const trends = asArray(intelligence.trend_based);
  const priorities = asArray(intelligence.decision_priorities);
  const hasContent = requested.summary || advised.summary || facts.length || trends.length || priorities.length;
  if (!hasContent) return null;
  return (
    <div className="mt-3 rounded-2xl border border-[#CFE8DD] bg-[#F4FBF7] p-3" data-testid="ai-destination-intelligence">
      <p className="flex items-center gap-2 text-sm font-black text-[#17211D]"><Sparkles className="h-4 w-4 text-[#2D7A65]" /> What I checked before asking</p>
      <div className="mt-3 grid gap-3 lg:grid-cols-2">
        <div className="rounded-xl border border-[#E2DFD7] bg-white p-3">
          <p className="text-[10px] font-black uppercase tracking-[0.12em] text-[#516259]">Your starting idea</p>
          <p className="mt-1 text-sm font-black text-[#17211D]">{requested.summary || "I'll keep your original preference visible."}</p>
          {requested.timing && <p className="mt-1 text-xs font-semibold text-[#516259]">Timing: {requested.timing}</p>}
          {asArray(requested.notes).length > 0 && <p className="mt-2 text-xs leading-5 text-[#516259]">{plainConsultantText(requested.notes.slice(0, 2).join(" "), 260)}</p>}
        </div>
        <div className="rounded-xl border border-[#BFE2D4] bg-white p-3">
          <p className="text-[10px] font-black uppercase tracking-[0.12em] text-[#2D7A65]">Stronger planning direction</p>
          <p className="mt-1 text-sm font-black text-[#17211D]">{advised.summary || "I'll compare your idea with a more reliable way to run this trip."}</p>
          {advised.timing && <p className="mt-1 text-xs font-semibold text-[#516259]">Best timing: {advised.timing}</p>}
          {asArray(advised.best_months).length > 0 && <p className="mt-1 text-xs font-semibold text-[#516259]">Best months: {advised.best_months.slice(0, 4).join(", ")}</p>}
        </div>
      </div>
      {(facts.length > 0 || trends.length > 0) && (
        <div className="mt-3 grid gap-3 lg:grid-cols-2">
          {facts.length > 0 && (
            <div className="rounded-xl border border-[#CFE8DD] bg-white p-3">
              <p className="text-[10px] font-black uppercase tracking-[0.12em] text-[#2D7A65]">Fact-based</p>
              <ul className="mt-1 space-y-1 text-xs leading-5 text-[#516259]">{facts.slice(0, 3).map((item) => <li key={item}>- {plainConsultantText(item, 150)}</li>)}</ul>
            </div>
          )}
          {trends.length > 0 && (
            <div className="rounded-xl border border-sky-200 bg-white p-3">
              <p className="text-[10px] font-black uppercase tracking-[0.12em] text-sky-800">Trend-based</p>
              <ul className="mt-1 space-y-1 text-xs leading-5 text-[#516259]">{trends.slice(0, 3).map((item) => <li key={item}>- {plainConsultantText(item, 150)}</li>)}</ul>
            </div>
          )}
        </div>
      )}
      {priorities.length > 0 && (
        <p className="mt-3 rounded-xl border border-[#E2DFD7] bg-white px-3 py-2 text-xs font-semibold leading-5 text-[#516259]">
          {plainConsultantText(priorities.slice(0, 2).join(" "), 240)}
        </p>
      )}
    </div>
  );
}

function GuidedPreflightPanel({ preflight, answers, setAnswers, selectedRecommendations, completedStepKeys, setCompletedStepKeys, onAnswerStep, onRecommendationStep, refreshing }) {
  if (!preflight) return null;
  const steps = preflightSteps(preflight, selectedRecommendations, completedStepKeys, answers);
  const activeStep = steps[0];
  const totalSteps = completedStepKeys.length + steps.length;
  const answeredSteps = completedStepKeys.length;
  const remainingAfterThis = Math.max(0, steps.length - 1);
  const markDone = (key) => setCompletedStepKeys((prev) => (prev.includes(key) ? prev : [...prev, key]));
  const activeQuestion = !refreshing && activeStep?.type === "question" ? activeStep.question : null;
  const activeGroup = !refreshing && activeStep?.type === "recommendation" ? activeStep.group : null;

  const finishQuestion = (question, value) => {
    const nextValue = value !== undefined ? value : answers[question.field];
    if (value !== undefined) setAnswers((prev) => ({ ...prev, [question.field]: value }));
    onAnswerStep(question, nextValue);
    markDone(`question-${question.field}`);
  };

  const chooseRecommendation = (group, guidance, alternatives) => {
    onRecommendationStep(guidance, alternatives, group);
    markDone(`recommendation-${group.key}`);
  };
  const factPack = preflight.fact_pack || {};
  const progressLabel = activeStep ? `${answeredSteps} of ${totalSteps}` : "Ready";
  const stepLabel = activeStep ? `Step ${answeredSteps + 1} of ${totalSteps}` : "";

  return (
    <div className="mt-5 space-y-4" data-testid="ai-preflight-panel">
      {refreshing && (
        <div className="rounded-2xl border border-[#CFE8DD] bg-[#F4FBF7] p-4 text-xs font-semibold leading-5 text-[#254F43]">
          <div className="flex items-center gap-2 font-black">
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
            Updating the next choice from what you picked
          </div>
        </div>
      )}

      {activeQuestion && (
        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4" data-testid="ai-followup-questions">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="flex items-center gap-2 text-sm font-black text-amber-950"><HelpCircle className="h-4 w-4" /> Quick question</p>
            <span className="rounded-full border border-amber-200 bg-white px-3 py-1 text-[11px] font-black text-amber-900">{progressLabel} answered</span>
          </div>
          <div className="mt-3 rounded-xl border border-amber-200 bg-white/70 p-3">
            <span className="mb-1 block text-sm font-black text-[#17211D]">{simpleQuestionText(activeQuestion.question)}</span>
            {activeQuestion.why && <span className="mb-2 block text-xs leading-5 text-[#516259]">{simpleWhyText(activeQuestion.why)}</span>}
            <QuestionInput
              question={activeQuestion}
              value={answers[activeQuestion.field]}
              onChange={(value) => setAnswers((prev) => ({ ...prev, [activeQuestion.field]: value }))}
              onCommit={(value) => finishQuestion(activeQuestion, value)}
            />
            <div className="mt-3 flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => finishQuestion(activeQuestion)}
                disabled={refreshing || (!isQuestionAnswered(activeQuestion, answers) && !isPlaceExplorationQuestion(activeQuestion))}
                className="inline-flex min-h-9 items-center gap-2 rounded-md border border-[#2D7A65] bg-[#2D7A65] px-3 text-xs font-bold text-white disabled:cursor-not-allowed disabled:opacity-50"
              >
                <Check className="h-3.5 w-3.5" /> Next
              </button>
              {isPlaceExplorationQuestion(activeQuestion) && (
                <button
                  type="button"
                  onClick={() => finishQuestion(activeQuestion, "Let AI suggest the best-fit route")}
                  className="inline-flex min-h-9 items-center rounded-md border border-amber-200 bg-white px-3 text-xs font-bold text-amber-900"
                >
                  Let AI suggest
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {activeGroup && (
        <div className="rounded-2xl border border-[#CFE8DD] bg-[#F4FBF7] p-4" data-testid="ai-recommendations">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <p className="flex items-center gap-2 text-sm font-black text-[#17211D]"><Sparkles className="h-4 w-4 text-[#2D7A65]" /> Choose what fits this trip</p>
              {stepLabel && <p className="mt-1 text-xs font-semibold text-[#516259]">{stepLabel}. {remainingAfterThis} more choice{remainingAfterThis === 1 ? "" : "s"} after this.</p>}
            </div>
            <span className="rounded-full border border-[#CFE8DD] bg-white px-3 py-1 text-[11px] font-black text-[#254F43]">{progressLabel} answered</span>
          </div>
          {activeGroup.key === "places_route_scope" && asArray(factPack.popular_routes).length > 0 && (
            <div className="mt-3 rounded-xl border border-[#CFE8DD] bg-white px-3 py-2 text-xs leading-5 text-[#516259]">
              <span className="font-black text-[#17211D]">Popular route people often take:</span> {plainConsultantText(factPack.popular_routes[0], 180)}
            </div>
          )}
          <div className="mt-3 rounded-xl border border-[#CFE8DD] bg-white p-3">
            {activeGroup.items.length > 1 && <p className="mb-2 text-xs font-bold text-[#2D7A65]">Pick one direction. The next step will follow this choice.</p>}
            <div className={`grid gap-3 ${activeGroup.items.length > 1 ? "md:grid-cols-2" : ""}`}>
              {activeGroup.items.map((rec) => {
                const guidance = rec.apply_text || rec.advice;
                const alternatives = recommendationGuidanceOptions(activeGroup, factPack);
                const selected = selectedRecommendations.includes(guidance);
                const evidenceBadges = evidenceBadgesFor(rec);
                const monthOptions = asArray(rec.month_options);
                const hasMonthSelector = activeGroup.key === "season_timing" && monthOptions.length > 0;
                const monthExperiences = monthExperiencesForRecommendation(rec, factPack);
                const choiceFacts = Array.from(new Set([
                  ...monthExperiences.map((item) => `${item.month}${item.verdict ? ` (${item.verdict})` : ""}: ${item.experience}`),
                  ...asArray(rec.facts),
                ])).filter(Boolean);
                const choiceBenefits = Array.from(new Set([
                  ...monthExperiences.flatMap((item) => asArray(item.pros)),
                  ...asArray(rec.benefits),
                ])).filter(Boolean);
                const choiceRisks = Array.from(new Set([
                  ...monthExperiences.flatMap((item) => asArray(item.cons)),
                  ...asArray(rec.risks),
                ])).filter(Boolean);
                return (
                  <div key={`${rec.title}-${rec.originalIndex}`} className={`rounded-lg border p-3 ${selected ? "border-[#2D7A65] bg-[#F4FBF7]" : "border-[#ECEAE4] bg-[#FFFCF7]"}`}>
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        {rec.option_label && <p className="mb-1 text-[11px] font-black uppercase tracking-[0.12em] text-[#516259]">{consultantOptionLabel(rec.option_label)}</p>}
                        <p className="text-sm font-black text-[#17211D]">{consultantTitle(rec.title)}</p>
                        {evidenceBadges.length > 0 && (
                          <div className="mt-2 flex flex-wrap gap-1.5">
                            {evidenceBadges.map((badge) => (
                              <span key={badge.label} className={`rounded-full border px-2 py-0.5 text-[10px] font-black uppercase tracking-[0.08em] ${badge.cls}`}>{badge.label}</span>
                            ))}
                          </div>
                        )}
                        <p className="mt-1 text-xs leading-5 text-[#516259]">{plainConsultantText(rec.advice, 230)}</p>
                        {rec.evidence && <p className="mt-1 text-[11px] font-semibold leading-5 text-[#2D7A65]">Why: {plainConsultantText(rec.evidence, 180)}</p>}
                         {hasMonthSelector && (
                           <label className="mt-3 block">
                             <span className="mb-1 block text-[10px] font-black uppercase tracking-[0.12em] text-[#516259]">Choose the month</span>
                             <select
                               className={inputCls}
                               defaultValue=""
                               onChange={(event) => {
                                 if (!event.target.value) return;
                                 const choice = seasonMonthChoice(rec, event.target.value, factPack);
                                 chooseRecommendation(activeGroup, choice.apply_text, alternatives);
                               }}
                             >
                               <option value="">Select a recommended month</option>
                               {monthOptions.map((month) => <option key={month} value={month}>{month}</option>)}
                               <option value="__ai__">Let AI choose the strongest month</option>
                             </select>
                           </label>
                         )}
                        {(choiceFacts.length > 0 || choiceBenefits.length > 0 || choiceRisks.length > 0 || rec.cost_impact || rec.operations_impact) && (
                          <details className="mt-2 rounded-md border border-[#ECEAE4] bg-white/70 p-2">
                            <summary className="cursor-pointer text-[11px] font-black uppercase tracking-[0.1em] text-[#516259]">Details</summary>
                            {choiceFacts.length > 0 && (
                              <div className="mt-2">
                                <p className="text-[10px] font-black uppercase tracking-[0.12em] text-[#516259]">What I checked</p>
                                <ul className="mt-1 space-y-1 text-[11px] leading-4 text-[#516259]">
                                  {choiceFacts.slice(0, 4).map((fact) => <li key={fact}>- {plainConsultantText(fact, 150)}</li>)}
                                </ul>
                              </div>
                            )}
                            {(choiceBenefits.length > 0 || choiceRisks.length > 0) && (
                              <div className="mt-2 grid gap-2 text-[11px] leading-4 md:grid-cols-2">
                                {choiceBenefits.length > 0 && (
                                  <div className="rounded-md bg-[#F4FBF7] p-2 text-[#254F43]">
                                    <p className="font-black">Good for you</p>
                                    <p>{choiceBenefits.slice(0, 3).map((item) => plainConsultantText(item, 100)).join(" | ")}</p>
                                  </div>
                                )}
                                {choiceRisks.length > 0 && (
                                  <div className="rounded-md bg-amber-50 p-2 text-amber-900">
                                    <p className="font-black">Things to know</p>
                                    <p>{choiceRisks.slice(0, 3).map((item) => plainConsultantText(item, 100)).join(" | ")}</p>
                                  </div>
                                )}
                              </div>
                            )}
                            {(rec.cost_impact || rec.operations_impact) && (
                              <p className="mt-2 text-[11px] leading-4 text-[#516259]">
                                {[rec.cost_impact, rec.operations_impact].filter(Boolean).map((item) => plainConsultantText(item, 140)).join(" ")}
                              </p>
                            )}
                          </details>
                        )}
                        {asArray(rec.source_links).length > 0 && (
                          <p className="mt-2 text-[10px] font-bold uppercase tracking-[0.1em] text-[#8B9A92]">
                            Source-backed facts included
                          </p>
                        )}
                      </div>
                      {!hasMonthSelector && (
                        <button
                          type="button"
                          onClick={() => chooseRecommendation(activeGroup, guidance, alternatives)}
                          disabled={refreshing}
                          className={`inline-flex min-h-9 items-center gap-2 rounded-md border px-3 text-xs font-bold ${selected ? "border-[#2D7A65] bg-[#2D7A65] text-white" : "border-[#CFE8DD] bg-white text-[#254F43] hover:border-[#2D7A65]"}`}
                        >
                          <Check className="h-3.5 w-3.5" /> {selected ? "Chosen" : activeGroup.items.length > 1 ? "Choose this" : "Use this"}
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {!activeStep && (
        <div className="rounded-2xl border border-[#CFE8DD] bg-[#F4FBF7] p-4" data-testid="ai-guided-complete">
          <p className="flex items-center gap-2 text-sm font-black text-[#17211D]"><Check className="h-4 w-4 text-[#2D7A65]" /> Ready to create the draft</p>
          <p className="mt-1 text-xs leading-5 text-[#516259]">The key questions and planning choices are handled. You can create the trip draft now.</p>
        </div>
      )}
    </div>
  );
}

function AiProgress({ mode, status, foundation, onReset, resetting }) {
  const [step, setStep] = useState(0);
  useEffect(() => {
    if (!mode) return undefined;
    setStep(0);
    const timer = window.setInterval(() => setStep((current) => current + 1), 2800);
    return () => window.clearInterval(timer);
  }, [mode]);

  if (!mode) return null;
  const config = PROGRESS_CONFIG[mode] || PROGRESS_CONFIG.generation;
  const activeIndex = step % config.stages.length;
  const activeStage = config.stages[activeIndex];
  const ActiveIcon = activeStage.icon;
  const activeTip = config.tips[step % config.tips.length];
  const place = String(foundation?.place || "").trim();
  return (
    <div className="mt-4 overflow-hidden rounded-2xl border border-[#BFE2D4] bg-[#F4FBF7] shadow-sm" data-testid="ai-progress">
      <div className="relative p-4">
        <div className="pointer-events-none absolute inset-x-0 top-0 h-1 bg-[#E4EEE9]">
          <div className="tp-ai-scan-line h-full rounded-full bg-[#2D7A65]" />
        </div>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-[11px] font-black uppercase tracking-[0.14em] text-[#2D7A65]">{config.eyebrow}</p>
            <h3 className="mt-1 text-base font-black text-[#17211D]">{status || config.title}</h3>
            <p className="mt-1 max-w-2xl text-xs leading-5 text-[#516259]">{config.detail}</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <div className="inline-flex items-center gap-2 rounded-full border border-[#CFE8DD] bg-white px-3 py-2 text-xs font-black text-[#254F43]">
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              Working live
            </div>
            {onReset && (
              <button
                type="button"
                onClick={onReset}
                disabled={resetting}
                data-testid="ai-start-fresh-running"
                className="inline-flex min-h-9 items-center gap-1.5 rounded-full border border-rose-200 bg-white px-3 text-xs font-black text-rose-700 hover:bg-rose-50 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {resetting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RotateCcw className="h-3.5 w-3.5" />}
                {resetting ? "Resetting..." : "Start fresh"}
              </button>
            )}
          </div>
        </div>

        <div className="mt-4 grid gap-3 lg:grid-cols-[0.9fr_1.1fr]">
          <div className="relative min-h-[132px] overflow-hidden rounded-xl border border-[#CFE8DD] bg-white p-4">
            <div className="absolute inset-0 tp-ai-shimmer" />
            <div className="relative">
              <div className="mb-3 flex items-center justify-between gap-3">
                <div>
                  <p className="text-xs font-black text-[#17211D]">{place ? `${place} planning board` : "Trip planning board"}</p>
                  <p className="mt-1 text-[11px] font-semibold text-[#516259]">Current focus: {activeStage.label}</p>
                </div>
                <ActiveIcon className="h-5 w-5 text-[#2D7A65]" />
              </div>
              <svg viewBox="0 0 320 76" className="h-20 w-full" role="img" aria-label="AI planning progress route">
                <path d="M20 48 C80 8 118 70 176 34 S264 18 300 50" fill="none" stroke="#D8D1C2" strokeWidth="8" strokeLinecap="round" />
                <path className="tp-ai-route-flow" d="M20 48 C80 8 118 70 176 34 S264 18 300 50" fill="none" stroke="#2D7A65" strokeWidth="3" strokeLinecap="round" />
                {[20, 104, 190, 300].map((x, index) => (
                  <circle key={x} className={index === activeIndex ? "tp-ai-route-ping" : ""} cx={x} cy={index === 1 ? 27 : index === 2 ? 37 : 48} r={index === activeIndex ? 8 : 6} fill={index <= activeIndex ? "#2D7A65" : "#FFFCF7"} stroke="#2D7A65" strokeWidth="3" />
                ))}
              </svg>
            </div>
          </div>

          <div className="grid gap-2 sm:grid-cols-2">
            {config.stages.map((stage, index) => {
              const StageIcon = stage.icon;
              const active = index === activeIndex;
              const done = index < activeIndex;
              return (
                <div key={stage.label} className={`rounded-xl border p-3 transition ${active ? "border-[#2D7A65] bg-white shadow-sm" : "border-[#CFE8DD] bg-white/70"}`}>
                  <div className="flex items-start gap-2">
                    <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${active ? "bg-[#2D7A65] text-white" : done ? "bg-[#E5B85C] text-[#17211D]" : "bg-[#E4EEE9] text-[#516259]"}`}>
                      {done ? <Check className="h-4 w-4" /> : <StageIcon className="h-4 w-4" />}
                    </span>
                    <div>
                      <p className="text-xs font-black text-[#17211D]">{stage.label}</p>
                      <p className="mt-0.5 text-[11px] leading-4 text-[#516259]">{stage.detail}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="mt-3 rounded-xl border border-[#E5B85C]/50 bg-[#FFF8E6] p-3">
          <div className="flex items-start gap-2">
            <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-[#B57916]" />
            <div>
              <p className="text-xs font-black text-[#5F3B00]">While you wait</p>
              <p className="mt-1 text-xs leading-5 text-[#6B4B1A]">{activeTip}</p>
            </div>
          </div>
        </div>
        <p className="mt-2 text-[11px] font-semibold text-[#516259]">Keep this page open. The result will appear here automatically.</p>
      </div>
    </div>
  );
}

function PlanReview({ plan, validation, selectedRecommendations = [], factPack = {}, editable = false, onPlanChange }) {
  const setField = (key, value) => onPlanChange?.(updatePlanField(plan, key, value));
  const setDay = (dayNumber, changes) => onPlanChange?.({
    ...plan,
    days: plan.days.map((day) => day.day_number === dayNumber ? { ...day, ...changes } : day),
  });
  const seasonContext = seasonChoiceContext(selectedRecommendations, factPack);
  const visibleInfeasibilities = asArray(plan.infeasibilities).filter((item) => !isStaleSeasonText(item, seasonContext));
  const validationIssues = asArray(validation?.issues);
  const highValidationIssues = validationIssues.filter((issue) => issue.severity === "high");
  const stayBases = Array.from(new Set(asArray(plan.accommodation).map((item) => item.location || item.name).filter(Boolean)));
  const costPerPerson = plan.cost_estimate?.per_person_minor || plan.budget_per_person?.amount_minor || 0;
  const selectedChoiceSnippets = asArray(selectedRecommendations).slice(0, 5).map((item) => String(item || "").replace(/\s+/g, " ").trim()).filter(Boolean);
  return (
    <div className="space-y-4" data-testid="ai-plan-review">
      <div className="overflow-hidden rounded-2xl border border-[#D8D1C2] bg-[#FFFCF7] shadow-sm">
        <div className="border-b border-[#ECEAE4] bg-white/70 p-5">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="mb-2 flex flex-wrap items-center gap-2">
                <GovBadge kind="ai" label="AI draft" />
              <span className="rounded-full border border-[#CFE8DD] bg-[#F4FBF7] px-2.5 py-1 text-[10px] font-black uppercase tracking-[0.1em] text-[#254F43]">Review before creating</span>
              </div>
              <h2 className="font-[Plus_Jakarta_Sans,sans-serif] text-2xl font-black leading-tight text-[#17211D]">{plan.title}</h2>
              <p className="mt-2 max-w-4xl text-sm leading-6 text-[#516259]">{plan.overview}</p>
            </div>
            <div className="rounded-xl border border-[#E2DFD7] bg-[#FFFAF1] px-4 py-3 text-right">
              <p className="text-[10px] font-black uppercase tracking-[0.14em] text-[#516259]">Estimated trip cost</p>
              <p className="mt-1 text-xl font-black text-[#17211D]">{inr(costPerPerson)}</p>
              <p className="text-[11px] font-semibold text-[#516259]">per person</p>
            </div>
          </div>
          <div className="mt-4 grid gap-2 sm:grid-cols-2 lg:grid-cols-5">
            <InfoMetric label="Region" value={plan.region} />
            <InfoMetric label="Duration" value={`${plan.duration_days || "?"} days`} />
            <InfoMetric label="Group" value={`${plan.group_size || "?"} pax`} />
            <InfoMetric label="Difficulty" value={plan.difficulty_grade} />
            <InfoMetric label="Stay bases" value={stayBases.length ? compactList(stayBases, 2) : "Pending"} tone={stayBases.length > 3 ? "warn" : "neutral"} />
          </div>
        </div>

        {editable && (
          <div className="m-5 grid gap-3 rounded-2xl border border-[#E2DFD7] bg-[#FFFAF1]/70 p-3 sm:grid-cols-2 lg:grid-cols-4">
            <label className="sm:col-span-2">
              <span className="mb-1 block text-[10px] font-black uppercase tracking-[0.14em] text-[#516259]">Trip title</span>
              <input className={inputCls} value={plan.title || ""} onChange={(event) => setField("title", event.target.value)} />
            </label>
            <label>
              <span className="mb-1 block text-[10px] font-black uppercase tracking-[0.14em] text-[#516259]">Region</span>
              <input className={inputCls} value={plan.region || ""} onChange={(event) => setField("region", event.target.value)} />
            </label>
            <label>
              <span className="mb-1 block text-[10px] font-black uppercase tracking-[0.14em] text-[#516259]">Difficulty</span>
              <select className={inputCls} value={plan.difficulty_grade || "moderate"} onChange={(event) => setField("difficulty_grade", event.target.value)}>
                <option value="easy">Easy</option>
                <option value="moderate">Moderate</option>
                <option value="challenging">Challenging</option>
                <option value="strenuous">Strenuous</option>
              </select>
            </label>
            <label>
              <span className="mb-1 block text-[10px] font-black uppercase tracking-[0.14em] text-[#516259]">Max seats</span>
              <input type="number" min="1" className={inputCls} value={plan.group_size || 1} onChange={(event) => setField("group_size", event.target.value)} />
            </label>
            <label>
              <span className="mb-1 block text-[10px] font-black uppercase tracking-[0.14em] text-[#516259]">Budget / person</span>
              <input type="number" min="0" className={inputCls} value={Math.round((plan.budget_per_person?.amount_minor || plan.cost_estimate?.per_person_minor || 0) / 100)} onChange={(event) => setField("budget", event.target.value)} />
            </label>
            <label className="sm:col-span-2 lg:col-span-4">
              <span className="mb-1 block text-[10px] font-black uppercase tracking-[0.14em] text-[#516259]">Overview</span>
              <textarea className={`${inputCls} min-h-24`} value={plan.overview || ""} onChange={(event) => setField("overview", event.target.value)} />
            </label>
          </div>
        )}
      </div>

      {(visibleInfeasibilities.length > 0 || validationIssues.length > 0 || selectedChoiceSnippets.length > 0) && (
        <div className="grid gap-3 lg:grid-cols-3">
          {visibleInfeasibilities.length > 0 && (
            <AttentionItem title="Important watch-outs" tone={highValidationIssues.length ? "danger" : "warn"}>
              <ul className="ml-4 list-disc">
                {visibleInfeasibilities.slice(0, 4).map((x, i) => <li key={i}>{consultantText(x)}</li>)}
              </ul>
            </AttentionItem>
          )}
          {validationIssues.length > 0 && (
            <AttentionItem title={`${validationIssues.length} thing${validationIssues.length === 1 ? "" : "s"} to check`}>
              {highValidationIssues.length > 0 ? `${highValidationIssues.length} important item${highValidationIssues.length === 1 ? "" : "s"} need attention. ` : ""}
              Check long days, rest points, timing conflicts, and missing route details before creating the trip.
            </AttentionItem>
          )}
          {selectedChoiceSnippets.length > 0 && (
            <div className="rounded-lg border border-[#CFE8DD] bg-[#F4FBF7] p-3 text-[#254F43]">
              <p className="flex items-center gap-1.5 text-xs font-black"><Check className="h-3.5 w-3.5" /> Choices used</p>
              <div className="mt-2 flex flex-wrap gap-1.5">
                {selectedChoiceSnippets.map((choice, index) => (
                  <span key={`${choice}-${index}`} className="rounded-full border border-[#BFE2D4] bg-white px-2 py-1 text-[10px] font-bold text-[#254F43]">{choice.slice(0, 90)}{choice.length > 90 ? "..." : ""}</span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {validation?.issues?.length > 0 && (
        <div className="rounded-2xl border border-amber-200 bg-[#FFF8E6] p-4" data-testid="ai-validation-panel">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <p className="flex items-center gap-1.5 text-sm font-black text-amber-900"><AlertTriangle className="h-4 w-4" /> Things to check before creating the trip</p>
              <p className="mt-1 max-w-3xl text-xs leading-5 text-amber-800">These checks do not block saving, but they help you catch rushed days, missing rest time, or weak route details before the trip goes live.</p>
            </div>
            <span className="rounded-full border border-amber-200 bg-white px-3 py-1 text-xs font-black text-amber-900">{validation.status || "needs review"}</span>
          </div>
          <ul className="mt-3 grid gap-2 text-sm text-amber-800 md:grid-cols-2">
            {validation.issues.slice(0, 8).map((issue, i) => (
              <li key={i} className="rounded-lg border border-amber-200 bg-white/70 p-2">
                <span className="font-black">{issue.day_number ? `Day ${issue.day_number}: ` : ""}</span>{consultantText(issue.message)}
              </li>
            ))}
          </ul>
          <p className="mt-2 text-xs text-amber-700">{validation.disclaimer}</p>
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <div className="space-y-4 lg:col-span-2">
          <Section icon={MapPin} title="Day-by-day route" action={<span className="text-xs font-bold text-[#516259]">{plan.days?.length || 0} days</span>}>
            <div className="space-y-3">
              {plan.days.map((d) => (
                <div key={d.day_number} className="rounded-xl border border-[#ECEAE4] bg-[#FFFCF7] p-3">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="flex items-start gap-2">
                      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#2D7A65] text-xs font-black text-white">D{d.day_number}</span>
                      <div>
                        <p className="font-black text-[#17211D]">{d.title}</p>
                        {d.summary && <p className="mt-1 text-xs leading-5 text-[#516259]">{d.summary}</p>}
                      </div>
                    </div>
                    {d.rest_day && <span className="rounded-full bg-sky-50 px-2 py-0.5 text-[10px] font-medium text-sky-700">REST</span>}
                  </div>
                  {editable && (
                    <div className="ml-9 mt-3 grid gap-2">
                      <input aria-label={`Day ${d.day_number} title`} className={inputCls} value={d.title || ""} onChange={(event) => setDay(d.day_number, { title: event.target.value })} />
                      <textarea aria-label={`Day ${d.day_number} summary`} className={`${inputCls} min-h-20`} value={d.summary || ""} onChange={(event) => setDay(d.day_number, { summary: event.target.value })} />
                    </div>
                  )}
                  <div className="ml-10 mt-3 grid gap-1.5">
                    {d.activities?.map((a, i) => (
                      <div key={i} className="rounded-lg border border-[#ECEAE4] bg-white px-3 py-2 text-xs text-[#516259]">
                        {a.start_time && <b className="mr-2 text-[#2D7A65]">{a.start_time}</b>}
                        <span className="font-bold text-[#17211D]">{a.name}</span>
                        {a.location ? <span> - {a.location}</span> : null}
                      </div>
                    ))}
                  </div>
                  <div className="ml-10 mt-3 flex flex-wrap gap-2 text-[11px] font-semibold text-[#516259]">
                    {d.accommodation && <span>Stay: {d.accommodation}</span>}
                    {d.transport && <span>Transport: {d.transport}</span>}
                    {d.meals?.length > 0 && <span>Meals: {d.meals.join(", ")}</span>}
                  </div>
                </div>
              ))}
            </div>
          </Section>

          <Section icon={RouteIcon} title="Route">
            <p className="text-sm text-slate-600">{plan.route?.summary}</p>
            {plan.route?.waypoints?.length > 0 && <p className="mt-2 rounded-lg border border-[#ECEAE4] bg-[#FFFCF7] p-3 text-xs font-semibold leading-5 text-[#516259]">{plan.route.waypoints.join(" -> ")}</p>}
            <div className="mt-3 grid gap-2 sm:grid-cols-3">
              {plan.route?.distance_km != null && <InfoMetric label="Distance" value={`${plan.route.distance_km} km`} />}
              {plan.route?.max_altitude_m != null && <InfoMetric label="Max altitude" value={`${plan.route.max_altitude_m} m`} />}
              {plan.route?.elevation_gain_m != null && <InfoMetric label="Elevation gain" value={`${plan.route.elevation_gain_m} m`} />}
            </div>
            {plan.route_suggestions?.length > 0 && <ul className="ml-5 mt-3 list-disc text-xs text-slate-500">{plan.route_suggestions.map((x, i) => <li key={i}>{x}</li>)}</ul>}
          </Section>

          {(plan.transport?.length > 0 || plan.accommodation?.length > 0 || plan.meals?.length > 0) && (
            <Section icon={MapPin} title="Operations plan">
              <div className="grid gap-2 md:grid-cols-3">
                <div className="rounded-lg border border-[#ECEAE4] bg-[#FFFCF7] p-3">
                  <p className="text-xs font-black text-[#17211D]">Transfers</p>
                  <div className="mt-2 space-y-1 text-xs leading-5 text-[#516259]">
                    {plan.transport?.slice(0, 5).map((t, i) => <p key={`t-${i}`}>{t.mode || t.type || "Transfer"} {t.from || t.from_location ? `from ${t.from || t.from_location}` : ""} {t.to || t.to_location ? `to ${t.to || t.to_location}` : ""}</p>)}
                    {!plan.transport?.length && <p>Pending</p>}
                  </div>
                </div>
                <div className="rounded-lg border border-[#ECEAE4] bg-[#FFFCF7] p-3">
                  <p className="text-xs font-black text-[#17211D]">Stays</p>
                  <div className="mt-2 space-y-1 text-xs leading-5 text-[#516259]">
                    {plan.accommodation?.slice(0, 5).map((a, i) => <p key={`a-${i}`}>{a.name || a.type} {a.location ? `in ${a.location}` : ""}</p>)}
                    {!plan.accommodation?.length && <p>Pending</p>}
                  </div>
                </div>
                <div className="rounded-lg border border-[#ECEAE4] bg-[#FFFCF7] p-3">
                  <p className="text-xs font-black text-[#17211D]">Meals</p>
                  <div className="mt-2 space-y-1 text-xs leading-5 text-[#516259]">
                    {plan.meals?.slice(0, 5).map((m, i) => <p key={`m-${i}`}>{m.plan}</p>)}
                    {!plan.meals?.length && <p>Pending</p>}
                  </div>
                </div>
              </div>
            </Section>
          )}

          <Section icon={Calendar} title="Preparation notes">
            <ul className="space-y-1 text-sm text-slate-600">{plan.tasks?.map((t, i) => <li key={i}>- {t.title}</li>)}</ul>
          </Section>
        </div>

        <div className="space-y-4">
          <Section icon={CircleDollarSign} title="Estimated cost">
            <p className="mb-2"><GovBadge kind="ai" label="Estimate, not final" /></p>
            <p className="text-3xl font-black text-[#17211D]">{inr(plan.cost_estimate?.per_person_minor)}<span className="text-xs font-normal text-slate-400"> / person</span></p>
            <p className="text-xs font-semibold text-[#516259]">Total {inr(plan.cost_estimate?.total_minor)} for {plan.group_size} pax</p>
            <div className="mt-3 space-y-1.5">
              {plan.cost_estimate?.line_items?.map((l, i) => (
                <div key={i} className="flex justify-between rounded-md bg-[#FFFCF7] px-2 py-1.5 text-xs text-[#516259]"><span>{l.label}</span><span className="font-black text-[#17211D]">{inr(l.amount_minor)}</span></div>
              ))}
            </div>
          </Section>
          <Section icon={Package} title="Equipment">
            <ul className="space-y-1 text-sm text-slate-600">{plan.equipment?.map((e, i) => <li key={i}>{e.mandatory ? "Required" : "Optional"}: {e.item}</li>)}</ul>
          </Section>
          {plan.packing_items?.length > 0 && (
            <Section icon={Package} title="Packing suggestions">
              <ul className="space-y-1 text-sm text-slate-600">{plan.packing_items.slice(0, 10).map((e, i) => <li key={i}>{e.name}</li>)}</ul>
            </Section>
          )}
          <Section icon={ShieldAlert} title="Participant requirements">
            <ul className="space-y-1 text-sm text-slate-600">{plan.participant_requirements?.map((r, i) => <li key={i}>- {r.label}{r.mandatory ? "" : " (optional)"}</li>)}</ul>
          </Section>
          <Section icon={ShieldAlert} title="Draft risks">
            <div className="space-y-2">{plan.draft_risks?.map((r, i) => (
              <div key={i} className="text-xs"><b className="text-[#1A2420]">{r.hazard}</b> <span className="text-slate-400">({r.likelihood}/{r.severity})</span><br /><span className="text-slate-500">{r.mitigation}</span></div>
            ))}</div>
          </Section>
          {plan.safety_notes?.length > 0 && (
            <Section icon={ShieldAlert} title="Safety notes">
              <ul className="space-y-1 text-sm text-slate-600">{plan.safety_notes.slice(0, 8).map((s, i) => <li key={i}>{s.note}</li>)}</ul>
              <p className="mt-2 text-xs text-slate-400">Review with qualified staff before sharing or operating.</p>
            </Section>
          )}
          <Section icon={FileText} title="Required documents">
            <ul className="space-y-1 text-sm text-slate-600">{plan.documents?.map((d, i) => <li key={i}>- {d.name}</li>)}</ul>
          </Section>
        </div>
      </div>
    </div>
  );
}

function FlightOptionsPanel({ options, selectedOption, loading, onRefresh, onChoose }) {
  const fareOptions = asArray(options?.options);
  if (!loading && !options) return null;
  const checked = !loading && fareOptions.length > 0;
  const failed = !loading && options && fareOptions.length === 0 && ["unavailable", "no_deals"].includes(options.status);
  return (
    <div className="overflow-hidden rounded-2xl border border-[#BFE2D4] bg-[#F4FBF7] shadow-sm" data-testid="ai-flight-options-panel">
      <div className="border-b border-[#CFE8DD] bg-white/75 p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="flex items-center gap-2 text-sm font-black text-[#17211D]"><Plane className="h-4 w-4 text-[#2D7A65]" /> Best flight dates selected from fare search</p>
          <p className="mt-1 text-xs leading-5 text-[#516259]">The cheapest returned date window is applied automatically. The other fare results remain available if you prefer a different window.</p>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {checked && <span className="rounded-full border border-[#BFE2D4] bg-[#F4FBF7] px-2 py-0.5 text-[10px] font-black uppercase tracking-[0.08em] text-[#254F43]">Checked live/cached fares</span>}
            {failed && <span className="rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[10px] font-black uppercase tracking-[0.08em] text-amber-900">Could not check fares</span>}
          </div>
        </div>
        <button type="button" onClick={onRefresh} disabled={loading} className="inline-flex min-h-9 items-center gap-2 rounded-md border border-[#CFE8DD] bg-white px-3 text-xs font-bold text-[#254F43] disabled:opacity-60">
          {loading && <Loader2 className="h-3.5 w-3.5 animate-spin" />} {loading ? "Checking fares" : "Refresh fares"}
        </button>
      </div>
      </div>
      <div className="p-4">
      {loading && <div className="mt-3 rounded-xl border border-[#CFE8DD] bg-white p-3 text-xs font-semibold text-[#516259]">Searching fare-friendly windows across the chosen travel period...</div>}
      {!loading && asArray(options?.warnings).length > 0 && (
        <div className="mt-3 rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs font-semibold leading-5 text-amber-800">{options.warnings.join(" ")}</div>
      )}
      {!loading && asArray(options?.monthsSearched).length > 0 && (
        <p className="mt-2 text-[11px] font-bold text-[#516259]">
          Months checked: {options.monthsSearched.join(", ")}
          {options.monthSelectionReason === "fact_priority_months" ? " - picked from current destination facts." : ""}
          {options.monthSelectionReason === "season_window_sample" ? " - sampled from the selected season window." : ""}
        </p>
      )}
      {!loading && options?.budgetAdvice && (
        <div className={`mt-3 rounded-xl border p-3 text-xs font-semibold leading-5 ${["over_budget", "tight"].includes(options.budgetStatus) ? "border-rose-200 bg-rose-50 text-rose-800" : "border-[#CFE8DD] bg-white text-[#254F43]"}`}>
          {options.budgetAdvice}
        </div>
      )}
      {!loading && fareOptions.length > 0 && (
        <div className="mt-3 grid gap-3 md:grid-cols-3">
          {fareOptions.slice(0, 3).map((option) => (
            <div key={`${option.departureDate}-${option.returnDate}-${option.price}`} className={`relative rounded-xl border p-4 transition ${selectedOption?.departureDate === option.departureDate && selectedOption?.returnDate === option.returnDate ? "border-[#2D7A65] bg-white shadow-sm ring-2 ring-[#BFE2D4]" : "border-[#CFE8DD] bg-white hover:border-[#2D7A65]"}`}>
              {selectedOption?.departureDate === option.departureDate && selectedOption?.returnDate === option.returnDate && <span className="absolute right-3 top-3 rounded-full bg-[#2D7A65] px-2 py-1 text-[10px] font-black text-white">Selected</span>}
              <p className="text-[10px] font-black uppercase tracking-[0.12em] text-[#2D7A65]">{option.label || `Option ${option.rank}`}</p>
              <p className="mt-1 pr-16 text-sm font-black text-[#17211D]">{option.departureDate} to {option.returnDate}</p>
              <p className="mt-1 text-xs font-semibold text-[#516259]">{option.tripDays} days - {options.origin} to {options.destination}</p>
              <p className="mt-2 text-lg font-black text-[#17211D]">{fareMoney(option.price, option.currency)}</p>
              {["over_budget", "tight"].includes(option.budgetStatus) && (
                <p className="mt-1 inline-flex rounded-full border border-rose-200 bg-rose-50 px-2 py-1 text-[10px] font-black uppercase tracking-[0.08em] text-rose-700">
                  Budget pressure
                </p>
              )}
              <p className={`mt-1 text-[11px] font-semibold leading-4 ${["over_budget", "tight"].includes(option.budgetStatus) ? "text-rose-700" : "text-[#2D7A65]"}`}>{option.budgetAdvice}</p>
              <div className="mt-3 flex flex-wrap gap-2">
                <button type="button" onClick={() => onChoose(option)} className="inline-flex min-h-9 items-center gap-2 rounded-md border border-[#2D7A65] bg-[#2D7A65] px-3 text-xs font-bold text-white">
                  <Check className="h-3.5 w-3.5" /> {selectedOption?.departureDate === option.departureDate && selectedOption?.returnDate === option.returnDate ? "Selected" : "Use instead"}
                </button>
                {option.liveFareUrl && <a href={option.liveFareUrl} target="_blank" rel="noreferrer" className="inline-flex min-h-9 items-center rounded-md border border-[#CFE8DD] bg-white px-3 text-xs font-bold text-[#254F43]">Open fare</a>}
              </div>
            </div>
          ))}
        </div>
      )}
      {!loading && options && fareOptions.length === 0 && (
        <p className="mt-3 rounded-xl border border-[#E2DFD7] bg-white p-3 text-xs font-semibold text-[#516259]">No ranked fare options yet. Add/confirm origin airport, destination gateway, duration, and travel period, then refresh fares.</p>
      )}
      {!loading && options?.pricingNote && <p className="mt-2 text-[11px] font-semibold text-[#516259]">{options.pricingNote}</p>}
      </div>
    </div>
  );
}

function HotelOptionsPanel({ options, selectedHotels, loading, onRefresh, onChoose }) {
  const bases = asArray(options?.stayBases);
  if (!loading && !options) return null;
  const chosenCount = bases.filter((base) => selectedHotels[hotelBaseKey(base)]).length;
  const basesWithOptions = bases.filter((base) => asArray(base.options).length > 0).length;
  const checked = !loading && basesWithOptions > 0;
  const failed = !loading && options && basesWithOptions === 0 && ["unavailable", "no_options"].includes(options.status);
  return (
    <div className="overflow-hidden rounded-2xl border border-[#BFE2D4] bg-[#F4FBF7] shadow-sm" data-testid="ai-hotel-options-panel">
      <div className="border-b border-[#CFE8DD] bg-white/75 p-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <p className="flex items-center gap-2 text-sm font-black text-[#17211D]"><MapPin className="h-4 w-4 text-[#2D7A65]" /> Best stays selected from hotel search</p>
            <p className="mt-1 text-xs leading-5 text-[#516259]">
              The top-ranked result for each stay base is applied automatically using rating, review confidence, route fit, budget fit, and group practicality.
            </p>
            <div className="mt-2 flex flex-wrap gap-1.5">
              {checked && <span className="rounded-full border border-[#BFE2D4] bg-[#F4FBF7] px-2 py-0.5 text-[10px] font-black uppercase tracking-[0.08em] text-[#254F43]">Checked live/cached hotels</span>}
              {failed && <span className="rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[10px] font-black uppercase tracking-[0.08em] text-amber-900">Could not check hotels</span>}
            </div>
            {basesWithOptions > 0 && <p className="mt-1 text-[11px] font-bold text-[#2D7A65]">{chosenCount} of {basesWithOptions} stay base{basesWithOptions === 1 ? "" : "s"} selected</p>}
          </div>
          <button type="button" onClick={onRefresh} disabled={loading} className="inline-flex min-h-9 items-center gap-2 rounded-md border border-[#CFE8DD] bg-white px-3 text-xs font-bold text-[#254F43] disabled:opacity-60">
            {loading && <Loader2 className="h-3.5 w-3.5 animate-spin" />} {loading ? "Checking hotels" : "Refresh hotels"}
          </button>
        </div>
      </div>
      <div className="p-4">
      {loading && <div className="mt-3 rounded-xl border border-[#CFE8DD] bg-white p-3 text-xs font-semibold text-[#516259]">Finding stay options for each overnight base using selected dates...</div>}
      {!loading && asArray(options?.warnings).length > 0 && (
        <div className="mt-3 rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs font-semibold leading-5 text-amber-800">{options.warnings.join(" ")}</div>
      )}
      {!loading && bases.length > 0 && (
        <div className="mt-4 space-y-4">
          {bases.map((base) => {
            const key = hotelBaseKey(base);
            const selected = selectedHotels[key];
            const hotelCards = asArray(base.options);
            return (
              <div key={key} className="rounded-xl border border-[#CFE8DD] bg-white p-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <p className="text-sm font-black text-[#17211D]">{base.location} - {base.nights} night{base.nights === 1 ? "" : "s"}</p>
                    <p className="text-xs font-semibold text-[#516259]">{base.checkIn || "date pending"} to {base.checkOut || "date pending"}</p>
                  </div>
                  {selected && <span className="rounded-full border border-[#CFE8DD] bg-[#F4FBF7] px-3 py-1 text-xs font-black text-[#254F43]">Hotel selected</span>}
                </div>
                {asArray(base.warnings).length > 0 && <p className="mt-2 text-xs font-semibold text-amber-800">{base.warnings.join(" ")}</p>}
                {hotelCards.length > 0 && (
                  <div className="mt-3 grid gap-3 lg:grid-cols-3">
                    {hotelCards.map((hotel) => {
                      const isSelected = selected?.name === hotel.name;
                      return (
                        <div key={`${key}-${hotel.name}-${hotel.price || "na"}`} className={`relative rounded-lg border p-3 transition ${isSelected ? "border-[#2D7A65] bg-[#F4FBF7] ring-2 ring-[#BFE2D4]" : "border-[#ECEAE4] bg-[#FFFCF7] hover:border-[#2D7A65]"}`}>
                          {isSelected && <span className="absolute right-3 top-3 rounded-full bg-[#2D7A65] px-2 py-1 text-[10px] font-black text-white">Selected</span>}
                          <p className="pr-16 text-sm font-black text-[#17211D]">{hotel.name}</p>
                          <p className="mt-1 line-clamp-2 text-xs font-semibold text-[#516259]">{hotel.address || base.location}</p>
                          <div className="mt-2 flex flex-wrap gap-2 text-[11px] font-bold text-[#516259]">
                            {hotel.rating && <span>Rating {hotel.rating}/5</span>}
                            {hotel.reviews > 0 && <span>{Number(hotel.reviews).toLocaleString("en-IN")} reviews</span>}
                            {hotel.price && <span>{fareMoney(hotel.price, hotel.currency)}</span>}
                          </div>
                          {asArray(hotel.facts).length > 0 && (
                            <div className="mt-2 rounded-md bg-white/70 p-2">
                              <p className="text-[10px] font-black uppercase tracking-[0.12em] text-[#2D7A65]">Why this stay works</p>
                              <ul className="mt-1 space-y-1 text-[11px] leading-4 text-[#516259]">
                                {hotel.facts.slice(0, 3).map((fact) => <li key={fact}>- {consultantText(fact)}</li>)}
                              </ul>
                            </div>
                          )}
                          {asArray(hotel.watchouts).length > 0 && (
                            <p className="mt-2 text-[11px] font-semibold leading-4 text-amber-800">Good to know: {hotel.watchouts.slice(0, 2).map(consultantText).join(" ")}</p>
                          )}
                          <div className="mt-3 flex flex-wrap gap-2">
                            <button type="button" onClick={() => onChoose(base, hotel)} className="inline-flex min-h-9 items-center gap-2 rounded-md border border-[#2D7A65] bg-[#2D7A65] px-3 text-xs font-bold text-white">
                              <Check className="h-3.5 w-3.5" /> {isSelected ? "Selected" : "Use instead"}
                            </button>
                            {hotel.link && <a href={hotel.link} target="_blank" rel="noreferrer" className="inline-flex min-h-9 items-center rounded-md border border-[#CFE8DD] bg-white px-3 text-xs font-bold text-[#254F43]">Open</a>}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
      {!loading && options?.pricingNote && <p className="mt-2 text-[11px] font-semibold text-[#516259]">{options.pricingNote}</p>}
      </div>
    </div>
  );
}

export default function AiPlanner() {
  const nav = useNavigate();
  const pollRef = useRef(null);
  const preflightAbortRef = useRef(null);
  const preflightRefreshRef = useRef(0);
  const resumeAttemptedRef = useRef(false);
  const [restoredDraft] = useState(() => rememberedPlannerDraftState());
  const [brief, setBrief] = useState(restoredDraft.brief || "");
  const [foundation, setFoundation] = useState({ ...DEFAULT_FOUNDATION, ...(restoredDraft.foundation || {}) });
  const [answers, setAnswers] = useState(restoredDraft.answers || {});
  const [selectedRecommendations, setSelectedRecommendations] = useState(restoredDraft.selectedRecommendations || []);
  const [completedPreflightSteps, setCompletedPreflightSteps] = useState(restoredDraft.completedPreflightSteps || []);
  const [preflight, setPreflight] = useState(restoredDraft.preflight || null);
  const [sessionId, setSessionId] = useState(null);
  const [genId, setGenId] = useState(null);
  const [plan, setPlan] = useState(null);
  const [validation, setValidation] = useState(null);
  const [changes, setChanges] = useState(null);
  const [instruction, setInstruction] = useState("");
  const [busy, setBusy] = useState(false);
  const [reviewing, setReviewing] = useState(false);
  const [refreshingChoices, setRefreshingChoices] = useState(false);
  const [status, setStatus] = useState("");
  const [progressMode, setProgressMode] = useState("");
  const [accepting, setAccepting] = useState(false);
  const [aiError, setAiError] = useState("");
  const [savedPlans, setSavedPlans] = useState([]);
  const [loadingSavedPlans, setLoadingSavedPlans] = useState(false);
  const [terminatingJobs, setTerminatingJobs] = useState(false);
  const [savingPlan, setSavingPlan] = useState(false);
  const [currentPlanSaved, setCurrentPlanSaved] = useState(false);
  const [similarPlanAdvice, setSimilarPlanAdvice] = useState(null);
  const [generationSubmitted, setGenerationSubmitted] = useState(false);
  const [flightOptions, setFlightOptions] = useState(null);
  const [selectedFlightOption, setSelectedFlightOption] = useState(null);
  const [loadingFlightOptions, setLoadingFlightOptions] = useState(false);
  const [hotelOptions, setHotelOptions] = useState(null);
  const [selectedHotels, setSelectedHotels] = useState({});
  const [loadingHotelOptions, setLoadingHotelOptions] = useState(false);

  const finalBrief = useMemo(
    () => buildGuidedBrief(brief, foundation, answers, selectedRecommendations, preflight),
    [answers, brief, foundation, preflight, selectedRecommendations],
  );
  const missingFoundation = useMemo(() => missingRequiredFoundation(foundation), [foundation]);
  const foundationReady = missingFoundation.length === 0;

  const toggleRecommendation = (guidance, alternatives = []) => {
    setSelectedRecommendations((prev) => (
      prev.includes(guidance)
        ? prev.filter((item) => item !== guidance)
        : [...prev.filter((item) => !alternatives.includes(item)), guidance]
    ));
  };

  const fetchSavedPlans = useCallback(async () => {
    setLoadingSavedPlans(true);
    try {
      const { data } = await api.get("/ai/itinerary/plans");
      setSavedPlans(asArray(data.plans));
    } catch {
      setSavedPlans([]);
    } finally {
      setLoadingSavedPlans(false);
    }
  }, []);

  useEffect(() => {
    fetchSavedPlans();
  }, [fetchSavedPlans]);

  useEffect(() => {
    if (!brief && !preflight && Object.values(foundation).every((value) => !value)) {
      return;
    }
    rememberPlannerDraftState({
      brief,
      foundation,
      answers,
      selectedRecommendations,
      completedPreflightSteps,
      preflight,
    });
  }, [answers, brief, completedPreflightSteps, foundation, preflight, selectedRecommendations]);

  useEffect(() => () => {
    if (pollRef.current) window.clearInterval(pollRef.current);
  }, []);

  const resetPlannerState = () => {
    forgetActiveGeneration(genId);
    forgetPlannerDraftState();
    setBrief("");
    setFoundation({ ...DEFAULT_FOUNDATION });
    setAnswers({});
    setSelectedRecommendations([]);
    setPlan(null);
    setGenId(null);
    setSessionId(null);
    setValidation(null);
    setChanges(null);
    setInstruction("");
    setAiError("");
    setCurrentPlanSaved(false);
    setPreflight(null);
    setCompletedPreflightSteps([]);
    setSimilarPlanAdvice(null);
    setGenerationSubmitted(false);
    setFlightOptions(null);
    setSelectedFlightOption(null);
    setHotelOptions(null);
    setSelectedHotels({});
    setLoadingFlightOptions(false);
    setLoadingHotelOptions(false);
    setBusy(false);
    setReviewing(false);
    setRefreshingChoices(false);
    setProgressMode("");
    setStatus("");
  };

  const startFresh = async () => {
    if (!confirmAction("Stop the current AI planning and start fresh? Your saved AI plans will remain available.")) return;
    setTerminatingJobs(true);
    preflightRefreshRef.current += 1;
    if (preflightAbortRef.current) {
      preflightAbortRef.current.abort();
      preflightAbortRef.current = null;
    }
    if (pollRef.current) {
      window.clearInterval(pollRef.current);
      pollRef.current = null;
    }
    resetPlannerState();
    try {
      const { data } = await api.post("/ai/itinerary/jobs/terminate-old");
      toast.success(data.terminated ? "AI planning stopped. Ready for a fresh plan." : "Ready for a fresh plan.");
    } catch (err) {
      const msg = formatApiErrorDetail(err.response?.data?.detail);
      toast.error(`The planner was reset here, but the server could not confirm cancellation: ${msg}`);
    } finally {
      setTerminatingJobs(false);
    }
  };

  const openSavedPlan = async (planId) => {
    setBusy(true);
    setProgressMode("");
    setAiError("");
    try {
      const { data } = await api.get(`/ai/itinerary/plans/${planId}`);
      setPlan(data.plan);
      setGenId(data.id);
      setSessionId(data.session_id);
      setValidation(data.extra?.validation || null);
      setBrief(data.brief || "");
      setChanges(null);
      setInstruction("");
      setCurrentPlanSaved(true);
      setSimilarPlanAdvice(null);
      setFlightOptions(null);
      setSelectedFlightOption(null);
      setHotelOptions(null);
      setSelectedHotels({});
      toast.success("AI plan opened");
    } catch (err) {
      const msg = formatApiErrorDetail(err.response?.data?.detail);
      setAiError(msg);
      toast.error(msg);
    } finally {
      setBusy(false);
      setProgressMode("");
      setStatus("");
    }
  };

  const handlePlanChange = (nextPlan) => {
    setPlan(nextPlan);
    setCurrentPlanSaved(false);
    setSimilarPlanAdvice(null);
    setSelectedFlightOption(null);
    setHotelOptions(null);
    setSelectedHotels({});
  };

  const poll = (generationId, onDone, onError) => {
    if (pollRef.current) window.clearInterval(pollRef.current);
    let tries = 0;
    let pollErrors = 0;
    const iv = setInterval(async () => {
      tries++;
      setStatus(`Working with the AI planner... (${tries * 2}s)`);
      try {
        const { data } = await api.get(`/ai/generations/${generationId}`);
        pollErrors = 0;
        if (data.status === "succeeded") { window.clearInterval(iv); pollRef.current = null; forgetActiveGeneration(generationId); onDone(data); }
        else if (data.status === "failed") { window.clearInterval(iv); pollRef.current = null; forgetActiveGeneration(generationId); onError(data.error || "The AI planner could not complete the request."); }
        else if (data.status === "cancelled") { window.clearInterval(iv); pollRef.current = null; forgetActiveGeneration(generationId); onError("The AI planner was stopped."); }
        else if (tries > 300) { window.clearInterval(iv); pollRef.current = null; onError("The AI planner is still running after several minutes. You can leave this page and return to AI Planner later."); }
        else if (tries > 70) setStatus("Validating the AI draft and checking route details...");
        else if (tries > 25) setStatus("Adding stays, transfers, costs, and safety notes...");
        else if (tries > 8) setStatus("Building the day-by-day route...");
      } catch (e) {
        pollErrors++;
        if (pollErrors < 5) {
          setStatus("Connection paused. The AI planner is still running; reconnecting...");
          return;
        }
        window.clearInterval(iv);
        pollRef.current = null;
        onError(formatApiErrorDetail(e.response?.data?.detail));
      }
    }, 2000);
    pollRef.current = iv;
  };

  const openGeneration = async (generationId) => {
    setBusy(true);
    setGenerationSubmitted(true);
    setProgressMode("generation");
    setStatus("Checking this AI draft...");
    setAiError("");
    try {
      const { data } = await api.get(`/ai/generations/${generationId}`);
      setGenId(data.id);
      setSessionId(data.session_id);
      setBrief(data.brief || "");
      if (data.status === "pending") {
        rememberActiveGeneration(data.id);
        setPlan(null);
        toast.message("This AI draft is still running. Tracking it now.");
        poll(data.id,
          (done) => {
            setGenId(done.id);
            setSessionId(done.session_id);
            setPlan(done.plan);
            setValidation(done.extra?.validation || null);
            setCurrentPlanSaved(false);
            setFlightOptions(null);
            setSelectedFlightOption(null);
            setHotelOptions(null);
            setSelectedHotels({});
            setBusy(false);
            setProgressMode("");
            setStatus("");
          },
          (det) => {
            const msg = formatApiErrorDetail(det);
            setAiError(msg);
            toast.error(msg);
            setBusy(false);
            setProgressMode("");
            setStatus("");
          });
        return;
      }
      if (data.status === "succeeded" && data.plan) {
        forgetActiveGeneration(data.id);
        setPlan(data.plan);
        setGenerationSubmitted(false);
        setValidation(data.extra?.validation || null);
        setChanges(null);
        setInstruction("");
        setCurrentPlanSaved(Boolean(data.saved));
        setSimilarPlanAdvice(null);
        setFlightOptions(null);
        setSelectedFlightOption(null);
        setHotelOptions(null);
        setSelectedHotels({});
        toast.success("AI draft opened");
      } else {
        forgetActiveGeneration(data.id);
        throw new Error(data.error || "The AI draft is not ready.");
      }
    } catch (err) {
      const msg = formatApiErrorDetail(err.response?.data?.detail || err.message);
      setAiError(msg);
      toast.error(msg);
    } finally {
      if (!pollRef.current) {
        setBusy(false);
        setProgressMode("");
        setStatus("");
      }
    }
  };

  useEffect(() => {
    if (resumeAttemptedRef.current) return;
    resumeAttemptedRef.current = true;
    const activeGenerationId = rememberedActiveGeneration();
    if (activeGenerationId) {
      openGeneration(activeGenerationId);
    }
    // openGeneration intentionally uses the initial mount state for one resume attempt.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const runPreflight = async ({ answersOverride, selectedOverride, quiet = false, adaptive = false, resetCompleted = true, statusText = "Checking your brief and preparing options..." } = {}) => {
    if (brief.trim().length < 3) {
      toast.error("Describe the trip you want");
      return null;
    }
    const nextAnswers = answersOverride || answers;
    const nextSelected = selectedOverride || selectedRecommendations;
    const requestId = preflightRefreshRef.current + 1;
    preflightRefreshRef.current = requestId;
    if (preflightAbortRef.current) preflightAbortRef.current.abort();
    const controller = new AbortController();
    preflightAbortRef.current = controller;
    if (adaptive) {
      setRefreshingChoices(true);
    } else {
      setRefreshingChoices(false);
      setReviewing(true);
      setProgressMode("preflight");
      setStatus(statusText);
    }
    setAiError("");
    try {
      const { data } = await api.post("/ai/itinerary/preflight", {
        brief,
        answers: preflightAnswers(foundation, nextAnswers),
        selected_recommendations: nextSelected,
      }, { signal: controller.signal });
      if (requestId === preflightRefreshRef.current) {
        setPreflight(data);
        if (resetCompleted) setCompletedPreflightSteps([]);
      }
      if (!quiet && blockingQuestions(data.missing_questions, nextAnswers).length > 0) {
        toast.success("A few details will make the draft better");
      }
      return data;
    } catch (err) {
      if (requestId !== preflightRefreshRef.current || err.code === "ERR_CANCELED") return null;
      const msg = formatApiErrorDetail(err.response?.data?.detail);
      if (!adaptive) {
        setAiError(msg);
        toast.error(msg);
      }
      return null;
    } finally {
      if (preflightAbortRef.current === controller) preflightAbortRef.current = null;
      if (requestId === preflightRefreshRef.current) {
        if (adaptive) {
          setRefreshingChoices(false);
        } else {
          setReviewing(false);
          setProgressMode("");
          setStatus("");
        }
      }
    }
  };

  const answerPreflightStep = (question, value) => {
    const nextAnswers = value !== undefined ? { ...answers, [question.field]: value } : answers;
    if (value !== undefined) {
      setAnswers(nextAnswers);
    }
    if (shouldRefreshAfterQuestion(question)) {
      runPreflight({
        answersOverride: nextAnswers,
        selectedOverride: selectedRecommendations,
        quiet: true,
        adaptive: true,
        resetCompleted: false,
      });
    }
  };

  const choosePreflightRecommendation = (guidance, alternatives = [], group = {}) => {
    const nextSelected = selectedRecommendations.includes(guidance)
      ? selectedRecommendations.filter((item) => item !== guidance)
      : [...selectedRecommendations.filter((item) => !alternatives.includes(item)), guidance];
    setSelectedRecommendations(nextSelected);
    setFlightOptions(null);
    setSelectedFlightOption(null);
    setHotelOptions(null);
    setSelectedHotels({});
    if (shouldRefreshAfterRecommendation(group, guidance)) {
      window.setTimeout(() => {
        runPreflight({
          answersOverride: answers,
          selectedOverride: nextSelected,
          quiet: true,
          adaptive: true,
          resetCompleted: false,
        });
      }, 0);
    }
  };

  const loadFlightOptions = useCallback(async () => {
    if (!preflight) return null;
    setLoadingFlightOptions(true);
    try {
      const { data } = await api.post("/ai/itinerary/flight-options", {
        brief,
        answers: preflightAnswers(foundation, answers),
        selected_recommendations: selectedRecommendations,
        preflight,
        plan,
      });
      const cheapestOption = asArray(data.options).reduce((best, option) => {
        if (!best) return option;
        return Number(option.price ?? Number.MAX_SAFE_INTEGER) < Number(best.price ?? Number.MAX_SAFE_INTEGER) ? option : best;
      }, null);
      setFlightOptions(data);
      setSelectedFlightOption(cheapestOption);
      setHotelOptions(null);
      setSelectedHotels({});
      if (cheapestOption) {
        setFoundation((prev) => ({
          ...prev,
          start_date: cheapestOption.departureDate || prev.start_date,
          end_date: cheapestOption.returnDate || prev.end_date,
        }));
      }
      return data;
    } catch (err) {
      const msg = formatApiErrorDetail(err.response?.data?.detail) || "Could not check flight fares.";
      setFlightOptions({ status: "unavailable", options: [], warnings: [msg], budgetAdvice: "Flight fares are date-sensitive and can be added after the route timing is chosen." });
      return null;
    } finally {
      setLoadingFlightOptions(false);
    }
  }, [answers, brief, foundation, plan, preflight, selectedRecommendations]);

  const chooseFlightOption = (option) => {
    setSelectedFlightOption(option);
    setHotelOptions(null);
    setSelectedHotels({});
    setFoundation((prev) => ({ ...prev, start_date: option.departureDate || prev.start_date, end_date: option.returnDate || prev.end_date }));
    toast.success("Flight dates applied to the draft");
  };

  const loadHotelOptions = useCallback(async () => {
    if (!plan || !selectedFlightOption) return null;
    setLoadingHotelOptions(true);
    try {
      const { data } = await api.post("/ai/itinerary/hotel-options", {
        plan,
        answers: preflightAnswers(foundation, answers),
        selected_flight_option: selectedFlightOption,
        preflight,
      });
      const automaticSelections = {};
      asArray(data.stayBases).forEach((base) => {
        const topHotel = asArray(base.options)[0];
        if (topHotel) automaticSelections[hotelBaseKey(base)] = topHotel;
      });
      setHotelOptions(data);
      setSelectedHotels(automaticSelections);
      if (Object.keys(automaticSelections).length > 0) {
        setPlan((current) => asArray(data.stayBases).reduce((nextPlan, base) => (
          applyHotelChoiceToPlan(nextPlan, base, automaticSelections[hotelBaseKey(base)])
        ), current));
        setCurrentPlanSaved(false);
      }
      return data;
    } catch (err) {
      const msg = formatApiErrorDetail(err.response?.data?.detail) || "Could not check hotels.";
      setHotelOptions({ status: "unavailable", stayBases: [], warnings: [msg], pricingNote: "Hotel options are date-sensitive and can be added after the route timing is chosen." });
      return null;
    } finally {
      setLoadingHotelOptions(false);
    }
  }, [answers, foundation, plan, preflight, selectedFlightOption]);

  const chooseHotelOption = (base, hotel) => {
    const key = hotelBaseKey(base);
    setSelectedHotels((prev) => ({ ...prev, [key]: hotel }));
    setPlan((prev) => applyHotelChoiceToPlan(prev, base, hotel));
    setCurrentPlanSaved(false);
    toast.success(`${hotel.name} selected for ${base.location}`);
  };

  useEffect(() => {
    if (plan && preflight && !flightOptions && !loadingFlightOptions) {
      loadFlightOptions();
    }
  }, [flightOptions, loadFlightOptions, loadingFlightOptions, plan, preflight]);

  useEffect(() => {
    if (plan && selectedFlightOption && !hotelOptions && !loadingHotelOptions) {
      loadHotelOptions();
    }
  }, [hotelOptions, loadHotelOptions, loadingHotelOptions, plan, selectedFlightOption]);

  const startGeneration = async (briefForGeneration) => {
    setBusy(true); setGenerationSubmitted(true); setProgressMode("generation"); setChanges(null); setAiError(""); setStatus("Designing your itinerary...");
    try {
      const { data } = await api.post("/ai/itinerary/generate", { brief: briefForGeneration });
      setSessionId(data.session_id);
      setGenId(data.generation_id);
      rememberActiveGeneration(data.generation_id);
      poll(data.generation_id,
        (d) => {
          setGenId(d.id);
          setPlan(d.plan);
          setValidation(d.extra?.validation || null);
          setCurrentPlanSaved(false);
          setGenerationSubmitted(false);
          setFlightOptions(null);
          setSelectedFlightOption(null);
          setHotelOptions(null);
          setSelectedHotels({});
          toast.success("Draft plan generated");
          setBusy(false);
          setProgressMode("");
          setStatus("");
        },
        (det) => {
          const msg = formatApiErrorDetail(det);
          setAiError(msg);
          toast.error(msg);
          setBusy(false);
          setProgressMode("");
          setStatus("");
        });
    } catch (err) {
      const msg = formatApiErrorDetail(err.response?.data?.detail);
      setAiError(msg);
      toast.error(msg);
      setBusy(false);
      setProgressMode("");
      setStatus("");
    }
  };

  const generate = async () => {
    if (brief.trim().length < 3) return toast.error("Describe the trip you want");
    if (!foundationReady) {
      toast.error(`Complete required basics first: ${missingFoundation.slice(0, 3).map((field) => field.label).join(", ")}${missingFoundation.length > 3 ? "..." : ""}`);
      return;
    }
    if (foundation.start_date && foundation.end_date && foundation.end_date < foundation.start_date) {
      return toast.error("End date cannot be before start date");
    }
    if (!preflight) {
      const checked = await runPreflight();
      if (!checked) return;
      const nextSteps = preflightSteps(checked, selectedRecommendations, [], answers);
      if (nextSteps.length > 0) {
        toast.message(`Review ${nextSteps.length} optional choice${nextSteps.length === 1 ? "" : "s"}, or create the draft when ready`);
        return;
      }
      return startGeneration(buildGuidedBrief(brief, foundation, answers, selectedRecommendations, checked));
    }
    return startGeneration(finalBrief);
  };

  const refine = async (text) => {
    const ins = text || instruction;
    if (!ins.trim()) return;
    setBusy(true); setAiError(""); setStatus("Applying your change...");
    try {
      const { data } = await api.post("/ai/itinerary/refine", { session_id: sessionId, generation_id: genId, instruction: ins });
      setGenId(data.generation_id);
      rememberActiveGeneration(data.generation_id);
      poll(data.generation_id,
        (d) => {
          setGenId(d.id);
          setPlan(d.plan);
          setValidation(d.extra?.validation || null);
          setInstruction("");
          setChanges({ changes: d.extra?.changes || [], affected: d.extra?.affected_dependencies || [] });
          setCurrentPlanSaved(false);
          setSimilarPlanAdvice(null);
          setFlightOptions(null);
          setSelectedFlightOption(null);
          setHotelOptions(null);
          setSelectedHotels({});
          toast.success("Plan updated");
          setBusy(false);
          setStatus("");
        },
        (det) => {
          const msg = formatApiErrorDetail(det);
          setAiError(msg);
          toast.error(msg);
          setBusy(false);
          setStatus("");
        });
    } catch (err) {
      const msg = formatApiErrorDetail(err.response?.data?.detail);
      setAiError(msg);
      toast.error(msg);
      setBusy(false);
      setStatus("");
    }
  };

  const persistCurrentPlan = async () => {
    const { data } = await api.post(`/ai/itinerary/plans/${genId}/save`, { plan });
    setPlan(data.plan);
    setValidation(data.extra?.validation || null);
    setCurrentPlanSaved(true);
    setSimilarPlanAdvice(null);
    await fetchSavedPlans();
    toast.success("AI plan saved");
  };

  const saveCurrentPlan = async ({ skipSimilarityCheck = false } = {}) => {
    if (!genId || !plan) return toast.error("Create or open an AI plan first");
    setSavingPlan(true);
    setAiError("");
    try {
      if (!skipSimilarityCheck && !currentPlanSaved) {
        const { data: similarity } = await api.post(`/ai/itinerary/plans/${genId}/similar`, { plan });
        if (similarity.should_advise_existing && asArray(similarity.matches).length > 0) {
          setSimilarPlanAdvice(similarity.matches[0]);
          toast.message("A matching saved AI plan already exists");
          return;
        }
      }
      await persistCurrentPlan();
    } catch (err) {
      const msg = formatApiErrorDetail(err.response?.data?.detail);
      setAiError(msg);
      toast.error(msg);
    } finally {
      setSavingPlan(false);
    }
  };

  const accept = async () => {
    if (!genId) return toast.error("Generate a plan before creating the trip");
    if (asArray(flightOptions?.options).length > 0 && !selectedFlightOption) {
      toast.error("The cheapest flight window is still being applied. Try again in a moment.");
      return;
    }
    const hotelBasesWithOptions = asArray(hotelOptions?.stayBases).filter((base) => asArray(base.options).length > 0);
    const missingHotelBase = hotelBasesWithOptions.find((base) => !selectedHotels[hotelBaseKey(base)]);
    if (hotelBasesWithOptions.length > 0 && missingHotelBase) {
      toast.error(`The best hotel for ${missingHotelBase.location} is still being applied. Try again in a moment.`);
      return;
    }
    if (!foundation.start_date || !foundation.end_date) return toast.error("Add trip start and end dates before creating the trip");
    if (foundation.start_date && foundation.end_date && foundation.end_date < foundation.start_date) return toast.error("End date cannot be before start date");
    const fareNote = selectedFlightOption ? `\n\nSelected fare window: ${selectedFlightOption.departureDate} to ${selectedFlightOption.returnDate}, ${fareMoney(selectedFlightOption.price, selectedFlightOption.currency)}. Flight fares can change before booking, so final budget may change.` : "";
    const hotelNote = hotelBasesWithOptions.length ? `\n\nSelected hotels: ${Object.values(selectedHotels).map((hotel) => hotel.name).join(", ")}. Hotel rates and availability can change until booking is confirmed.` : "";
    if (!confirmAction(`Create a trip from this AI draft?${fareNote}${hotelNote}`)) return;
    setAccepting(true);
    try {
      const { data } = await api.post("/ai/itinerary/accept", {
        generation_id: genId,
        plan,
        start_date: foundation.start_date || null,
        end_date: foundation.end_date || null,
      });
      toast.success("Trip created from AI plan"); nav(`/trips/${data.trip_id}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
      setAccepting(false);
    }
  };

  const generateButtonContent = () => {
    if (refreshingChoices) {
      return <><Loader2 className="h-4 w-4 animate-spin" /> Updating next step...</>;
    }
    if (busy || reviewing) {
      return <><Loader2 className="h-4 w-4 animate-spin" /> {reviewing ? "Checking..." : "Creating..."}</>;
    }
    if (!foundationReady) return <><Check className="h-4 w-4" /> Complete required basics</>;
    if (!preflight) return <><Sparkles className="h-4 w-4" /> Check trip idea</>;
    return <><Sparkles className="h-4 w-4" /> Create trip draft</>;
  };
  const generationHasError = Boolean(generationSubmitted && aiError && !busy && !reviewing);
  const showSubmittedSummary = Boolean(preflight) || busy || reviewing || generationHasError;
  const summaryMode = progressMode || (reviewing ? "preflight" : busy ? "generation" : "ready");
  const showGuidedChoices = Boolean(preflight && !busy && !reviewing && !generationHasError);
  const showPrimaryAction = !busy && !reviewing && !generationHasError;

  return (
    <AppLayout>
      <div className="tp-page-wrap max-w-5xl">
        <button onClick={() => nav("/trips")} data-testid="ai-back" className="mb-3 flex min-h-10 items-center gap-1 rounded-xl text-sm font-bold text-[#516259] hover:text-[#2D7A65]"><ArrowLeft className="h-4 w-4" /> Trips</button>
        <div className="tp-hero mb-6 flex items-center gap-4 p-6">
          <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-[#E5B85C]"><Sparkles className="h-6 w-6 text-[#17211D]" /></span>
          <div>
            <p className="mb-2 text-xs font-extrabold uppercase tracking-[0.26em] text-[#E5B85C]">Adventure copilot</p>
            <h1 className="font-[Plus_Jakarta_Sans,sans-serif] text-4xl font-extrabold leading-none">AI Trip Planner</h1>
            <p className="text-sm text-slate-500">Write your trip idea. We'll check what's missing and suggest improvements before creating the trip.</p>
          </div>
        </div>

        {!plan && (
          <SavedPlansPanel plans={savedPlans} loading={loadingSavedPlans} onOpen={openSavedPlan} />
        )}

        {!plan && (
          <div className="tp-paper rounded-[2rem] p-6">
            {showSubmittedSummary ? (
              <CompactGenerationSummary
                brief={brief}
                foundation={foundation}
                answers={answers}
                selectedRecommendations={selectedRecommendations}
                preflight={preflight}
                mode={summaryMode}
              />
            ) : (
              <>
                <textarea data-testid="ai-brief-input" aria-label="Trip brief" rows={4} className={inputCls} placeholder={EXAMPLE} value={brief} onChange={(e) => { setBrief(e.target.value); setPreflight(null); setCompletedPreflightSteps([]); }} />
                <div className="mt-2 flex flex-wrap gap-2">
                  <button type="button" onClick={() => { setBrief(EXAMPLE); setPreflight(null); setCompletedPreflightSteps([]); }} className="text-xs text-[#2D7A65] hover:underline">Use example</button>
                </div>
                <div className="mt-4 rounded-2xl border border-[#E2DFD7] bg-[#FFFAF1]/70 p-4">
                  <div className="mb-3 flex flex-wrap items-end justify-between gap-2">
                    <div>
                      <p className="text-sm font-black text-[#17211D]">Required basics</p>
                      <p className="mt-1 text-xs leading-5 text-[#516259]">Fill these before AI checks the trip idea, so it does not ask obvious questions later.</p>
                    </div>
                    <span className={`rounded-full border px-3 py-1 text-[11px] font-black ${foundationReady ? "border-[#CFE8DD] bg-[#F4FBF7] text-[#254F43]" : "border-amber-200 bg-amber-50 text-amber-900"}`}>
                      {foundationReady ? "Ready to check" : `${missingFoundation.length} missing`}
                    </span>
                  </div>
                  <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                    {FOUNDATION_FIELDS.map((field) => (
                      <label key={field.key} className={field.key === "place" ? "sm:col-span-2" : ""}>
                        <span className="mb-1 block text-[10px] font-black uppercase tracking-[0.14em] text-[#516259]">{field.label}</span>
                        {field.type === "select" ? (
                          <select
                            className={inputCls}
                            value={foundation[field.key]}
                            onChange={(event) => { setFoundation({ ...foundation, [field.key]: event.target.value }); setPreflight(null); setCompletedPreflightSteps([]); }}
                          >
                            <option value="">Choose</option>
                            {field.options.map((option) => <option key={option} value={option}>{option}</option>)}
                          </select>
                        ) : (
                          <input
                            className={inputCls}
                            type={field.type || "text"}
                            min={field.type === "number" ? "1" : undefined}
                            value={foundation[field.key]}
                            onChange={(event) => { setFoundation({ ...foundation, [field.key]: event.target.value }); setPreflight(null); setCompletedPreflightSteps([]); }}
                            placeholder={field.placeholder}
                          />
                        )}
                      </label>
                    ))}
                  </div>
                </div>

                <div className="mt-4 rounded-2xl border border-[#E2DFD7] bg-white/60 p-4">
                  <p className="mb-2 text-sm font-black text-[#17211D]">Add more detail</p>
                  <div className="flex flex-wrap gap-2">
                    {DETAIL_CHIPS.map((chip) => (
                      <button
                        key={chip.value}
                        type="button"
                        onClick={() => toggleRecommendation(chip.value)}
                        title={chip.helper}
                        className={`rounded-xl border px-3 py-2 text-left transition ${selectedRecommendations.includes(chip.value) ? "border-[#2D7A65] bg-[#2D7A65] text-white" : "border-[#E2DFD7] bg-[#FFFCF7] text-slate-600 hover:border-[#2D7A65] hover:text-[#2D7A65]"}`}
                      >
                        <span className="block text-xs font-black">{chip.label}</span>
                        <span className={`mt-0.5 block text-[11px] font-semibold leading-4 ${selectedRecommendations.includes(chip.value) ? "text-white/80" : "text-[#516259]"}`}>{chip.helper}</span>
                      </button>
                    ))}
                  </div>
                </div>
              </>
            )}
            {showGuidedChoices && (
              <GuidedPreflightPanel
                preflight={preflight}
                answers={answers}
                setAnswers={setAnswers}
                selectedRecommendations={selectedRecommendations}
                completedStepKeys={completedPreflightSteps}
                setCompletedStepKeys={setCompletedPreflightSteps}
                onAnswerStep={answerPreflightStep}
                onRecommendationStep={choosePreflightRecommendation}
                refreshing={reviewing || refreshingChoices}
              />
            )}
            {showPrimaryAction && (
              <div className="mt-4 flex flex-wrap gap-2">
                <Btn data-testid="ai-generate-button" onClick={generate} disabled={busy || reviewing || refreshingChoices}>
                  {generateButtonContent()}
                </Btn>
                {showSubmittedSummary && (
                  <Btn variant="secondary" onClick={startFresh} disabled={terminatingJobs} data-testid="ai-start-fresh">
                    <RotateCcw className="h-4 w-4" /> Start fresh
                  </Btn>
                )}
              </div>
            )}
            {(busy || reviewing) && (
              <AiProgress
                mode={progressMode || (reviewing ? "preflight" : "generation")}
                status={status}
                foundation={foundation}
                onReset={startFresh}
                resetting={terminatingJobs}
              />
            )}
            {aiError && (
              <div className="mt-4 space-y-3">
                <ErrorState message={aiError} onRetry={preflight ? generate : runPreflight} />
                <Btn variant="secondary" onClick={startFresh} disabled={terminatingJobs} data-testid="ai-start-fresh-error">
                  <RotateCcw className="h-4 w-4" /> Start fresh
                </Btn>
              </div>
            )}
          </div>
        )}

        {plan && (
          <div className="space-y-5">
            {aiError && <ErrorState message={aiError} />}
            <div className="tp-glass sticky top-[96px] z-30 rounded-[1.5rem] p-4 md:top-[88px]">
              <div className="flex flex-wrap items-center gap-2">
                <input data-testid="ai-refine-input" aria-label="Refine AI plan" className={inputCls} placeholder="Ask for a change, e.g. Make Day 3 easier" value={instruction} onChange={(e) => setInstruction(e.target.value)} onKeyDown={(e) => e.key === "Enter" && refine()} disabled={busy} />
                <Btn variant="secondary" data-testid="ai-refine-button" aria-label="Submit AI refinement" onClick={() => refine()} disabled={busy || !instruction.trim()}>{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}</Btn>
                <Btn variant="secondary" data-testid="ai-save-plan-button" onClick={saveCurrentPlan} disabled={savingPlan || busy}>{savingPlan ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} {currentPlanSaved ? "Saved" : "Save AI plan"}</Btn>
                <Btn data-testid="ai-accept-button" onClick={accept} disabled={accepting || busy || loadingFlightOptions || loadingHotelOptions}>{accepting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} Create trip from plan</Btn>
                <Btn variant="secondary" onClick={startFresh} disabled={accepting || savingPlan || terminatingJobs} data-testid="ai-start-fresh"><RotateCcw className="h-4 w-4" /> Start fresh</Btn>
              </div>
              <div className="mt-3 grid gap-2 sm:grid-cols-2">
                <label>
                  <span className="mb-1 block text-[10px] font-black uppercase tracking-[0.14em] text-[#516259]">Trip start</span>
                  <input type="date" className={inputCls} value={foundation.start_date} onChange={(event) => setFoundation({ ...foundation, start_date: event.target.value })} />
                </label>
                <label>
                  <span className="mb-1 block text-[10px] font-black uppercase tracking-[0.14em] text-[#516259]">Trip end</span>
                  <input type="date" min={foundation.start_date || undefined} className={inputCls} value={foundation.end_date} onChange={(event) => setFoundation({ ...foundation, end_date: event.target.value })} />
                </label>
              </div>
              <div className="mt-2 flex flex-wrap gap-1.5">
                {QUICK.map((q) => <button key={q} data-testid={`ai-quick-${q.replace(/\s+/g, "-").toLowerCase()}`} onClick={() => refine(q)} disabled={busy} className="rounded-full border border-[#E2DFD7] px-2.5 py-1 text-xs text-slate-600 hover:border-[#2D7A65] hover:text-[#2D7A65] disabled:opacity-50">{q}</button>)}
              </div>
            </div>

            {changes && (changes.changes.length > 0 || changes.affected.length > 0) && (
              <div className="rounded-lg border border-sky-200 bg-sky-50 p-4" data-testid="ai-changes-panel">
                {changes.changes.length > 0 && <><p className="text-sm font-semibold text-sky-800">What changed</p><ul className="ml-5 list-disc text-sm text-sky-700">{changes.changes.map((c, i) => <li key={i}>{c.day_number ? `Day ${c.day_number}: ` : ""}{c.description}</li>)}</ul></>}
                {changes.affected.length > 0 && <><p className="mt-2 text-sm font-semibold text-sky-800">Affected dependencies</p><p className="text-sm text-sky-700">{changes.affected.join(" - ")}</p></>}
              </div>
            )}

            {similarPlanAdvice && (
              <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4" data-testid="ai-similar-plan-advice">
                <p className="text-sm font-black text-amber-950">A matching saved AI plan already exists</p>
                <p className="mt-1 text-xs leading-5 text-amber-800">
                  Use <b>{similarPlanAdvice.name || similarPlanAdvice.title}</b> unless you intentionally want another copy. Match reason: {similarPlanAdvice.similarity?.reasons?.join(", ") || "same important trip details"}.
                </p>
                <div className="mt-3 flex flex-wrap gap-2">
                  <Btn variant="secondary" onClick={() => openSavedPlan(similarPlanAdvice.id)} disabled={busy || savingPlan}>Open existing plan</Btn>
                  <Btn onClick={() => saveCurrentPlan({ skipSimilarityCheck: true })} disabled={savingPlan || busy}>
                    {savingPlan ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save new plan anyway
                  </Btn>
                </div>
              </div>
            )}

            <FlightOptionsPanel
              options={flightOptions}
              selectedOption={selectedFlightOption}
              loading={loadingFlightOptions}
              onRefresh={loadFlightOptions}
              onChoose={chooseFlightOption}
            />

            <HotelOptionsPanel
              options={hotelOptions}
              selectedHotels={selectedHotels}
              loading={loadingHotelOptions}
              onRefresh={loadHotelOptions}
              onChoose={chooseHotelOption}
            />

            <PlanReview plan={plan} validation={validation} selectedRecommendations={selectedRecommendations} factPack={preflight?.fact_pack || {}} editable onPlanChange={handlePlanChange} />
            <p className="pb-8 text-center text-xs text-slate-400">
              This is an AI-generated draft. Costs are projected estimates. {currentPlanSaved ? "This plan is saved under AI Planner." : "Save the plan to keep it under AI Planner."}
            </p>
          </div>
        )}
      </div>
    </AppLayout>
  );
}
